/**
 * FlowAdapter — live flow execution state exposed as a connector resource.
 *
 * Holds the canonical `FlowExecution` snapshot for a single run and bridges
 * three ownership roles:
 *
 *  - The **agent** drives nodes to completion by calling tools (`start_node`,
 *    `request_approval`, `request_input`, `complete_node`, `skip_node`,
 *    `fail_node`, plus read-only `get_state` / `get_available` / `get_globals` /
 *    `build_handoff`).
 *  - The **runtime** drains functions through `NodeExecutor` and parks first-class
 *    gates without executing a node or starting a model.
 *  - The **host** runner applies out-of-turn
 *    mutations (`applyApproval`, `applyInput`, `retryNode`,
 *    `setAutonomousMode`, `cancel`, `hydrate`) in response to platform actions,
 *    subscribes to `onChange` for persistence, and uses the session stimulus bus
 *    to serialize permitted model turns.
 *
 * Every state mutation runs through a single `mutate()` helper that clones
 * the current snapshot, applies the change, recomputes derived fields via
 * `computeFlowStateFromSnapshots`, and fires the onChange callback with
 * the full FlowExecution shape. Consumers downstream (platform gateway,
 * frontend hook) see latest-wins snapshots.
 *
 * See `docs/flow-execution.md` for the universal model.
 */

import {
  AbstractConnector,
  type ConnectContext,
  type ConnectorContent,
  type ConnectorDeclaration,
  type ConnectorEntry,
  type ConnectorHandle,
  type ListOptions,
  type OperationDescriptor,
  type SearchOptions,
  type SearchResult,
  type ToolFace,
} from "@skaile/workspaces/connectors";
import type {
  Approval,
  ArtifactRef,
  CheckEvidence,
  FlowExecution,
  FlowExecutionStatus,
  InputRequest,
  InputSchema,
  NodeError,
  NodeExecution,
  NodeExecutionSnapshot,
  NodeOutput,
  NodeOutputCorrection,
  NodeStatus,
} from "@skaile/workspaces/types";
import * as z from "zod";
import type { BindingOrigin } from "./contract/expression.js";
import { coerceInputSchema, INPUT_SCHEMA_SHAPES_HINT } from "./contract/input-schema.js";
import { parseFlowDefinition } from "./contract/normalize.js";
import {
  appendAttempt,
  computeFlowStateFromSnapshots,
  countFailedAttempts,
  deriveCheckEvidence,
  type FlowDefinition,
  type FlowNode,
  getConsumedOutputSchema,
  hasAutomaticRetryBudget,
  interpolateNodeInstruction,
  isBlocking,
  remainingActiveExecutionMs,
  resolveNodeBindings,
  resolveTerminalFailureStatus,
  validateConsumedNodeOutput,
} from "./engine/index.js";
import {
  deterministicNodeKindLabel,
  flowDefaults,
  isAgentNode,
  isActiveRuntimeNode,
  isCheckNode,
  isFunctionNode,
  isGateNode,
  isRouterNode,
  isSubFlowNode,
  isSubpromptNode,
  nodeAssets,
  nodeInstruction,
  nodeIsOptional,
  nodeLabel,
  nodeRequiresApproval,
} from "./engine/node-shape.js";
import {
  availableRouterFrontier,
  evaluateRouterSelection,
  persistedRouterSelection,
  planRouterSkips,
  routerSelectionOutput,
  unsettledRouterBranchIds,
  type RouterSettlement,
} from "./engine/router-settlement.js";
import {
  ABORT_SIGKILL_GRACE_MS,
  type DeterministicFlowNode,
  type NodeExecutor,
  type NodeExecutorResult,
} from "./node-executor.js";
import type {
  InlineSubFlowOperationTarget,
  InlineSubFlowProjection,
  InlineSubFlowRunner,
} from "./sub-flow-runner.js";
import type { SubpromptFlowNode, SubpromptRunner } from "./subprompt-runner.js";
import {
  renderStimulusPrompt,
  renderSubpromptPrompt,
  type TurnStimulus,
} from "./prompt-fragments.js";
import { computeStimulus } from "./stimulus-driver.js";

// ── Stimulus bus surface ─────────────────────────────────────────────────────

/**
 * Minimal structural shape of the runner's `SessionStimulusBus`. Declared
 * locally so the adapter doesn't pull in a runtime dep on
 * `@skaile/workspaces/runner` (which would create a circular workspace import).
 *
 * The runner's `SessionStimulusBus` from `runner/src/session-stimulus.ts`
 * satisfies this structurally; mocks in tests can implement it directly.
 *
 * @docLink packages/factory-assets/flow#session-stimulus-bus
 */
export interface FlowAdapterStimulusBus {
  signal(
    connectorId: string,
    sig: { promptFragment: string; meta?: Record<string, unknown> },
  ): Promise<void>;
}

// ── State ───────────────────────────────────────────────────────────────────

/**
 * Internal state stored in the `ConnectorHandle` for the flow adapter.
 * @docLink packages/connectors/concepts#flow-adapter-state
 */
export interface FlowAdapterState {
  /** The flow graph definition this adapter is executing. */
  flowDef: FlowDefinition;
  /** Current execution snapshot (authoritative; mutated in place by `mutate()`). */
  execution: FlowExecution;
  /** Callback fired after every state mutation for host persistence and event forwarding. */
  onChange?: (state: FlowExecution) => void;
  /**
   * Optional `SessionStimulusBus` reference. When provided AND
   * `useBusForStimulus` is true, the adapter computes a `TurnStimulus`
   * after every mutation and fires `bus.signal()` to wake the agent.
   * The runner enables this for the serialized session-stimulus turn driver.
   */
  stimulusBus?: FlowAdapterStimulusBus;
  /**
   * Connector ID used as the `stimulusBus.signal()` queue key. Defaults to
   * the adapter handle's declaration ID (e.g. `flow:<runId>` in serve mode,
   * `flow` in CLI mode).
   */
  connectorId?: string;
  /** See {@link FlowAdapterConnectOptions.useBusForStimulus}. */
  useBusForStimulus?: boolean;
  /**
   * Last execution snapshot the adapter signaled to the bus. Used to compute
   * a structural diff in `computeStimulus`, so back-to-back mutations that
   * leave the snapshot unchanged don't fire spurious turn kicks.
   */
  lastSignaledExecution?: FlowExecution;
  /** Deterministic runtime backed by the already-materialized session workspace. */
  nodeExecutor?: NodeExecutor;
  /** Isolated model-call runtime for subprompt nodes — a second driver instance, never the session driver. */
  subpromptRunner?: SubpromptRunner;
  /** Host-owned inline child lifecycle, backed by the existing materialized session. */
  inlineSubFlowRunner?: InlineSubFlowRunner;
  /** Transient child operation routes keyed by their owning parent sub-flow node. */
  inlineOperationTargets: Map<string, InlineSubFlowOperationTarget>;
  /** Active disposable subprompt calls, cancelled and joined with their owning flow generation. */
  subpromptOperations: Map<string, { controller: AbortController; promise: Promise<unknown> }>;
  /** Cancelled child operations that the next asynchronous lifecycle boundary must join. */
  subpromptCleanup: Set<Promise<unknown>>;
  /** Non-durable provenance overrides for values received from a parent sub-flow binding. */
  flowInputOrigins?: Record<string, BindingOrigin[]>;
  /** Non-durable ancestry used to reject recursive inline delegation. */
  inlineSubFlowAncestry: string[];
  /** Non-durable ownership epoch for results returned by in-flight inline children. */
  executionGeneration: number;
  /**
   * Non-durable opportunistic wake timer for agent-node deadline re-evaluation.
   * The derived deadline (see `engine/node-deadline.ts`) is the source of truth;
   * this timer's only job is to trigger a re-evaluation call while nothing else
   * would (a genuinely hung agent turn has no other event to react to). Rearmed
   * after every mutation, `connect()`, and `hydrate()`; cleared on `disconnect()`.
   */
  wakeTimer: ReturnType<typeof setTimeout> | null;
}

/**
 * Options the runner passes via `declaration.options` when connecting the `FlowAdapter`.
 * @docLink packages/connectors/concepts#flow-adapter-connect-options
 */
export interface FlowAdapterConnectOptions {
  flow: FlowDefinition;
  /**
   * Either a fresh start seed (runId + startedBy) or a full rehydration
   * state. If `execution` is provided, it replaces the default empty
   * state — used on cold-container rehydration when the runner bootstraps
   * the flow connector from a host `connector_mutate { id: "flow",
   * op: "hydrate", payload: { state, flow } }` (Protocol >= 3.5).
   */
  execution?: FlowExecution;
  seed?: {
    runId: string;
    startedBy: string;
    autonomousMode?: boolean;
  };
  /**
   * Optional `SessionStimulusBus` to which this adapter should fire
   * `signal()` calls on every mutation when `useBusForStimulus` is true.
   * Threaded through by the runner's session-construction code (`runFlow`
   * / `serve.ts`).
   */
  stimulusBus?: FlowAdapterStimulusBus;
  /**
   * When `true` AND `stimulusBus` is provided, the
   * adapter computes a turn-kicking stimulus after every mutation and
   * calls `bus.signal()` (fire-and-forget). When `false` (the default),
   * only the `onChange` callback fires. Default: `false`.
   */
  useBusForStimulus?: boolean;
  /** Runtime for function nodes. Required before deterministic nodes are drained. */
  nodeExecutor?: NodeExecutor;
  /** Runtime for subprompt nodes. Required before subprompt nodes are drained. */
  subpromptRunner?: SubpromptRunner;
  /** Host lifecycle used to resolve and drive inline child flows in the current session. */
  inlineSubFlowRunner?: InlineSubFlowRunner;
  /** @internal Provenance for child flow inputs, retained only in the live adapter. */
  flowInputOrigins?: Record<string, BindingOrigin[]>;
  /** @internal Flow identities from the root through this transient child. */
  inlineSubFlowAncestry?: string[];
}

// ── Helpers ─────────────────────────────────────────────────────────────────

const now = () => new Date().toISOString();
const AGENT_INLINE_OPERATIONS = new Set([
  "get_available",
  "get_state",
  "get_globals",
  "build_handoff",
  "validate_node",
  "start_node",
  "request_approval",
  "request_input",
  "complete_node",
  "skip_node",
  "fail_node",
]);

/** Raised by {@link raceWithTimeout} when the timeout side of the race wins. */
class NodeTimeoutError extends Error {}

/**
 * Races `promise` against a `ms`-millisecond timer. Portable — plain
 * `setTimeout`/`clearTimeout`, no `Bun.spawn`/`Bun.serve`, safe under fake
 * timers.
 *
 * Does not cancel `promise` — it stops *waiting* on it. Cancelling is the
 * caller's job, on the seams that support it: the deterministic branch aborts
 * its per-attempt `AbortController` (which `SessionNodeExecutor` honors by
 * killing the process group) and the sub-flow branch cancels its inline target.
 * The subprompt seam still has none, so a timed-out subprompt keeps running.
 * A late settlement is swallowed here
 * (attached `.catch(() => {})`) so it can never surface as an unhandled
 * rejection or write state; the caller additionally re-checks
 * `executionGeneration` after this resolves so a late settlement that
 * raced past the timeout can never be mistaken for the timeout's own
 * result.
 *
 * @docLink packages/factory-assets/flow#race-with-timeout
 */
function raceWithTimeout<T>(promise: Promise<T>, ms: number, timeoutMessage: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new NodeTimeoutError(timeoutMessage));
      promise.catch(() => {});
    }, ms);
    timer.unref?.();
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

/**
 * How long an aborted deterministic attempt may take to die before the next one
 * is allowed to start. Outlasts the executor's own SIGTERM→SIGKILL escalation,
 * since a shorter budget would let the retry race the process it just killed.
 */
const ABORTED_ATTEMPT_SETTLE_MS = ABORT_SIGKILL_GRACE_MS + 1_000;

/**
 * Resolves when `promise` settles or `ms` elapses, whichever is first, and
 * never rejects. Bounded on purpose: an executor that ignores its abort signal
 * must not be able to wedge the drain loop waiting for work it never stops.
 */
function settleWithin(promise: Promise<unknown>, ms: number): Promise<void> {
  return new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, ms);
    timer.unref?.();
    const settled = () => {
      clearTimeout(timer);
      resolve();
    };
    promise.then(settled, settled);
  });
}

function mergeInteractionHistory<T extends { requestedAt: string }>(
  history: T[],
  interaction: T | undefined,
): T[] {
  if (!interaction) return history;
  const next = [...history];
  const index = next.findIndex((entry) => entry.requestedAt === interaction.requestedAt);
  if (index >= 0) next[index] = interaction;
  else next.push(interaction);
  return next;
}

function normalizeRuntimeFlow(authored: unknown): FlowDefinition {
  const parsed = parseFlowDefinition(authored);
  if (parsed.ok) return parsed.data;
  const details = parsed.errors
    .map((issue) => `${issue.path || "<root>"}: ${issue.message}`)
    .join("; ");
  throw new Error(`FlowAdapter: invalid flow definition: ${details}`);
}

/** Produce an empty NodeExecution for a node id. */
function emptyNode(nodeId: string): NodeExecution {
  return {
    id: nodeId,
    status: "not_started",
    approvalHistory: [],
    inputHistory: [],
    errorHistory: [],
    outputHistory: [],
  };
}

/** Build a prototype-safe node map so every valid authored node ID remains an own key. */
function nodeExecutionRecord(
  entries: Iterable<readonly [string, NodeExecution]> = [],
): Record<string, NodeExecution> {
  const record = Object.create(null) as Record<string, NodeExecution>;
  for (const [nodeId, execution] of entries) record[nodeId] = execution;
  return record;
}

/**
 * Build the starting FlowExecution snapshot for a flow definition — every
 * active runtime node begins as `not_started`; engine recompute promotes it.
 */
function buildInitialExecution(
  flowDef: FlowDefinition,
  seed: { runId: string; startedBy: string; autonomousMode?: boolean },
): FlowExecution {
  const nodes = nodeExecutionRecord();
  for (const n of flowDef.nodes) {
    if (isActiveRuntimeNode(n)) {
      nodes[n.id] = emptyNode(n.id);
    }
  }
  return {
    runId: seed.runId,
    flowId: flowDef.id,
    flowVersion: flowDef.version,
    status: "running",
    startedAt: now(),
    startedBy: seed.startedBy,
    autonomousMode: seed.autonomousMode ?? false,
    nodes,
    focus: [],
    done: false,
  };
}

function toSnapshot(exec: NodeExecution): NodeExecutionSnapshot {
  return {
    status: exec.status,
    approval: exec.approval,
    input: exec.input,
    output: exec.output,
    error: exec.error,
    failedPreconditions: exec.failedPreconditions,
    inputOrigins: exec.inputOrigins,
  };
}

function snapshotMap(execution: FlowExecution): Map<string, NodeExecutionSnapshot> {
  const map = new Map<string, NodeExecutionSnapshot>();
  for (const [id, exec] of Object.entries(execution.nodes)) {
    map.set(id, toSnapshot(exec));
  }
  return map;
}

function prepareExecutionSnapshot(
  flowDef: FlowDefinition,
  execution: FlowExecution,
): FlowExecution {
  let json: string;
  try {
    json = JSON.stringify(execution);
  } catch {
    throw new Error("FlowAdapter: execution snapshot must be a JSON object");
  }
  const cloned = z.json().safeParse(JSON.parse(json));
  if (!cloned.success || typeof cloned.data !== "object" || Array.isArray(cloned.data)) {
    throw new Error("FlowAdapter: execution snapshot must be a JSON object");
  }
  const snapshot = cloned.data as unknown as FlowExecution;
  for (const [nodeId, node] of Object.entries(snapshot.nodes ?? {})) {
    if (node.status !== "complete") continue;
    const definitionNode = flowDef.nodes.find(({ id }) => id === nodeId);
    if (
      definitionNode &&
      isRouterNode(definitionNode) &&
      !persistedRouterSelection(definitionNode, node)
    ) {
      throw new Error(`FlowAdapter: invalid execution output for router node ${nodeId}`);
    }
    const validation = validateConsumedNodeOutput(flowDef, nodeId, node.output?.fields);
    if (!validation.valid) {
      throw new Error(
        `FlowAdapter: invalid execution output for node ${nodeId}: ${validation.issues.join("; ")}`,
      );
    }
  }
  const nodes = nodeExecutionRecord(Object.entries(snapshot.nodes ?? {}));
  for (const node of flowDef.nodes) {
    const existing = nodes[node.id];
    // A subprompt's in-flight isolated driver call is exactly as unresumable
    // across a cold hydrate as an inline sub-flow's live child — reset it back
    // to `not_started` so admission restarts it rather than leaving it wedged
    // `running` forever. It only ever reaches `running` here (never the two
    // interaction states), but sharing the condition costs nothing.
    if (
      (isSubFlowNode(node) || isSubpromptNode(node)) &&
      existing &&
      ["running", "awaiting_approval", "awaiting_input"].includes(existing.status)
    ) {
      nodes[node.id] = {
        ...existing,
        status: "not_started",
        startedAt: undefined,
        completedAt: undefined,
        output: undefined,
        approval: undefined,
        input: undefined,
        error: undefined,
        // Moves with `output`: the node restarts from scratch, so origins that
        // described the discarded attempt would be stale.
        inputOrigins: undefined,
      };
      continue;
    }
    if (isActiveRuntimeNode(node) && !Object.hasOwn(nodes, node.id)) {
      nodes[node.id] = emptyNode(node.id);
    }
  }
  snapshot.nodes = nodes;
  return snapshot;
}

/**
 * Fail-fast helper. Throws a string Error so the SDK surfaces the reason
 * back to the agent tool-call log.
 */
function assertLegal(cond: unknown, message: string): asserts cond {
  if (!cond) throw new Error(`FlowAdapter: ${message}`);
}

function findNode(flowDef: FlowDefinition, nodeId: string): FlowNode {
  const node = flowDef.nodes.find((n) => n.id === nodeId);
  if (!node) throw new Error(`FlowAdapter: unknown nodeId ${nodeId}`);
  return node;
}

function isOptional(flowDef: FlowDefinition, nodeId: string): boolean {
  return nodeIsOptional(findNode(flowDef, nodeId));
}

/**
 * A mandatory agent-node approval policy always requires a human decision —
 * autonomous mode cannot auto-complete the agent node from `running`.
 */
function requiresAgentApproval(flowDef: FlowDefinition, nodeId: string): boolean {
  return nodeRequiresApproval(findNode(flowDef, nodeId));
}

// ── Adapter ─────────────────────────────────────────────────────────────────

/**
 * Connector adapter that holds and drives a live `FlowExecution`.
 *
 * The agent owns agent-node tools, the runtime drains function nodes and parks first-class
 * gates, the host applies durable human decisions, and the runner subscribes to state changes
 * to decide whether a model turn is needed. Every mutation runs through one recomputation
 * pipeline and publishes the resulting snapshot.
 * @docLink packages/connectors/concepts#flow-adapter
 */
export class FlowAdapter extends AbstractConnector {
  readonly name = "flow";
  readonly mountable = false;

  tools: ToolFace;

  constructor() {
    super();
    this.tools = {
      read: (handle, path) => this.read(handle, path),
      list: (handle, path, options) => this.list(handle, path, options),
      search: (handle, query, options) => this.search(handle, query, options),
      describeOperations: (handle) => this.describeOperations(handle),
      executeOp: (handle, operation, args) => this.executeOp(handle, operation, args),
    };
  }

  // ── Lifecycle ──

  async connect(declaration: ConnectorDeclaration, _ctx: ConnectContext): Promise<ConnectorHandle> {
    const options = (declaration.options ?? {}) as unknown as FlowAdapterConnectOptions;
    if (!options.flow) throw new Error("FlowAdapter requires options.flow (FlowDefinition)");
    const flowDef = normalizeRuntimeFlow(options.flow);

    const execution = options.execution
      ? prepareExecutionSnapshot(flowDef, options.execution)
      : buildInitialExecution(
          flowDef,
          options.seed ?? { runId: "local", startedBy: "system", autonomousMode: false },
        );

    const state: FlowAdapterState = {
      flowDef,
      execution,
      stimulusBus: options.stimulusBus,
      connectorId: declaration.id,
      useBusForStimulus: options.useBusForStimulus ?? false,
      nodeExecutor: options.nodeExecutor,
      subpromptRunner: options.subpromptRunner,
      inlineSubFlowRunner: options.inlineSubFlowRunner,
      inlineOperationTargets: new Map(),
      subpromptOperations: new Map(),
      subpromptCleanup: new Set(),
      flowInputOrigins: options.flowInputOrigins,
      inlineSubFlowAncestry: options.inlineSubFlowAncestry ?? [flowDef.id],
      executionGeneration: 0,
      wakeTimer: null,
    };
    // Recompute derived fields (focus, done) once so callers see a
    // consistent shape even if no mutation has happened yet.
    state.execution = this.recompute(state);
    const handle = this.makeHandle(declaration, state);
    this.rearmWakeTimer(handle);
    return handle;
  }

  async disconnect(handle: ConnectorHandle): Promise<void> {
    const state = this.s(handle);
    state.executionGeneration += 1;
    await this.cancelSubprompts(state);
    this.clearWakeTimer(state);
    await this.cancelInlineTargets(state);
    handle.status = "disconnected";
  }

  private async cancelInlineTargets(s: FlowAdapterState): Promise<void> {
    const targets = [...s.inlineOperationTargets.values()];
    s.inlineOperationTargets.clear();
    for (const target of targets) await target.cancel();
  }

  private async cancelSubprompts(s: FlowAdapterState): Promise<void> {
    for (const operation of s.subpromptOperations.values()) {
      operation.controller.abort();
      s.subpromptCleanup.add(operation.promise);
      void operation.promise.then(
        () => s.subpromptCleanup.delete(operation.promise),
        () => s.subpromptCleanup.delete(operation.promise),
      );
    }
    s.subpromptOperations.clear();
    await Promise.allSettled([...s.subpromptCleanup]);
  }

  private projectInlineInteraction(
    handle: ConnectorHandle,
    parentNodeId: string,
    projection: InlineSubFlowProjection,
  ): void {
    this.mutate(
      handle,
      (s) => {
        const node = s.execution.nodes[parentNodeId];
        assertLegal(node, `inline projection: unknown parent node ${parentNodeId}`);
        s.execution.nodes[parentNodeId] = {
          ...node,
          status: projection.status,
          approval: projection.approval ?? node.approval,
          approvalHistory: mergeInteractionHistory(node.approvalHistory, projection.approval),
          input: projection.input ?? node.input,
          inputHistory: mergeInteractionHistory(node.inputHistory, projection.input),
        };
      },
      false,
    );
  }

  // ── State change subscription ──

  onStateChange(handle: ConnectorHandle, cb: (state: FlowExecution) => void): void {
    this.s(handle).onChange = cb;
  }

  /**
   * Replace the current execution snapshot with a host-provided one.
   * Used on container rehydration via the extended `configure` command.
   * Publishes only when compatibility preparation changes the supplied snapshot,
   * such as seeding a node kind activated after the snapshot was persisted.
   */
  hydrate(handle: ConnectorHandle, execution: FlowExecution): void {
    const s = this.s(handle);
    void this.cancelSubprompts(s);
    assertLegal(
      s.inlineOperationTargets.size === 0,
      "hydrate requires live inline children to be cancelled first",
    );
    const candidate = prepareExecutionSnapshot(s.flowDef, execution);
    const prepared = this.recompute({ ...s, execution: candidate });
    const changed = JSON.stringify(prepared) !== JSON.stringify(execution);
    s.executionGeneration += 1;
    s.execution = prepared;
    // Reconnect re-evaluates anything missed: the derived deadline is a pure
    // function of the snapshot, so a node whose timeout already elapsed while
    // this container was cold gets caught on the next rearm below rather than
    // waiting for its own wake timer (which does not survive a cold container).
    this.rearmWakeTimer(handle);
    if (changed && s.onChange) s.onChange(s.execution);
  }

  // ── Base operations (private — wired into tools face in constructor) ──

  private async read(handle: ConnectorHandle, path: string): Promise<ConnectorContent | null> {
    const s = this.s(handle);

    if (path === "" || path === "state") {
      return {
        data: JSON.stringify(s.execution, null, 2),
        contentType: "application/json",
      };
    }

    if (path.startsWith("node/")) {
      const nodeId = path.slice(5);
      const node = s.flowDef.nodes.find((n) => n.id === nodeId);
      if (!node) return null;
      const exec = s.execution.nodes[nodeId];
      return {
        data: JSON.stringify({ ...node, execution: exec ?? null }, null, 2),
        contentType: "application/json",
      };
    }

    return null;
  }

  private async list(
    handle: ConnectorHandle,
    _path?: string,
    _options?: ListOptions,
  ): Promise<ConnectorEntry[]> {
    const s = this.s(handle);
    const nodes: NodeExecution[] = Object.values(s.execution.nodes);
    return nodes.map((n) => ({
      name: n.id,
      path: `node/${n.id}`,
      type: "key" as const,
    }));
  }

  private async search(
    handle: ConnectorHandle,
    query: string,
    _options?: SearchOptions,
  ): Promise<SearchResult[]> {
    const s = this.s(handle);
    const q = query.toLowerCase();
    const nodes: NodeExecution[] = Object.values(s.execution.nodes);
    return nodes
      .filter((n) => n.id.toLowerCase().includes(q) || n.status.includes(q))
      .map((n) => ({ path: `node/${n.id}`, snippet: `${n.id} [${n.status}]` }));
  }

  // ── Custom operations (agent tool surface) ──

  private describeOperations(handle: ConnectorHandle): OperationDescriptor[] {
    const ops: OperationDescriptor[] = [
      {
        name: "get_available",
        description: "Get node IDs that are ready to run (all required predecessors complete).",
        accessLevel: "read",
        args: [],
      },
      {
        name: "get_state",
        description: "Get the full flow execution snapshot — nodes, statuses, approvals, inputs.",
        accessLevel: "read",
        args: [],
      },
      {
        name: "get_globals",
        description:
          "Get the flow's run-scoped globals (e.g. run_input payload for scheduled/params-bound runs).",
        accessLevel: "read",
        args: [],
      },
      {
        name: "build_handoff",
        description:
          "Build the selected agent node briefing: instruction, supplied bindings, required assets, and completed predecessor context.",
        accessLevel: "read",
        args: [
          {
            name: "nodeId",
            description: "Target node to build handoff context for",
            type: "string",
            required: true,
          },
        ],
      },
      {
        name: "validate_node",
        description: "Check whether a node is currently runnable.",
        accessLevel: "read",
        args: [
          {
            name: "nodeId",
            description: "Node ID to validate",
            type: "string",
            required: true,
          },
        ],
      },
    ];

    if (handle.access === "read-write") {
      ops.push(
        {
          name: "start_node",
          description: "Mark an available agent node as running (records startedAt).",
          accessLevel: "write",
          args: [
            { name: "nodeId", description: "Node ID to start", type: "string", required: true },
          ],
        },
        {
          name: "request_approval",
          description:
            "Request human approval for a running agent node, or replace a rejected first-class gate decision with a fresh pending request. Ends your turn — the runner yields until the user approves or rejects.",
          accessLevel: "write",
          args: [
            { name: "nodeId", description: "Node ID", type: "string", required: true },
            {
              name: "summary",
              description: "One-paragraph summary of what the user is approving.",
              type: "string",
              required: true,
            },
            {
              name: "artifacts",
              description:
                "JSON array of ArtifactRef objects. Each: { uri, connectorId, kind, lifetime, producedBy }.",
              type: "string",
              required: false,
            },
          ],
        },
        {
          name: "request_input",
          description:
            "Request typed input for an agent node. Ends your turn. Legal from `available`, `running`, or `awaiting_approval`; never legal for a first-class gate.",
          accessLevel: "write",
          args: [
            { name: "nodeId", description: "Node ID", type: "string", required: true },
            {
              name: "prompt",
              description: "Prompt shown to the user alongside the input widget.",
              type: "string",
              required: true,
            },
            {
              name: "schema",
              description: `JSON InputSchema: one of ${INPUT_SCHEMA_SHAPES_HINT}. A choice option may be a bare string or omit its id; anything a host could not render is refused rather than stored.`,
              type: "string",
              required: true,
            },
          ],
        },
        {
          name: "complete_node",
          description:
            "Mark an agent node as complete with structured output. Consumed fields must match the declared output schema; an agent gets one corrective call before failure. Requires `awaiting_approval` with an `approved` decision, OR `running` when autonomous mode is on. Agent nodes with mandatory approval always require the approved decision. First-class gates complete directly through host approval and never use this operation.",
          accessLevel: "write",
          args: [
            { name: "nodeId", description: "Node ID to complete", type: "string", required: true },
            {
              name: "summary",
              description: "2-3 sentence summary of what the node accomplished.",
              type: "string",
              required: true,
            },
            {
              name: "fields",
              description: "Named JSON object consumed by downstream nodes.",
              type: "json",
              required: false,
            },
            {
              name: "artifacts",
              description: "JSON array of ArtifactRef objects produced by this node.",
              type: "string",
              required: false,
            },
            {
              name: "concerns",
              description: "JSON array of flagged concerns.",
              type: "string",
              required: false,
            },
          ],
        },
        {
          name: "skip_node",
          description: "Skip an optional node. Only valid when the node has `optional: true`.",
          accessLevel: "write",
          args: [
            { name: "nodeId", description: "Node ID to skip", type: "string", required: true },
          ],
        },
        {
          name: "fail_node",
          description:
            "Mark a node as failed. `recoverable: true` transitions back to `available` so the runner can retry; `recoverable: false` is terminal — an optional node becomes `skipped` so its dependents still proceed, any other node becomes `failed`.",
          accessLevel: "write",
          args: [
            { name: "nodeId", description: "Node ID", type: "string", required: true },
            {
              name: "message",
              description: "Human-readable failure reason.",
              type: "string",
              required: true,
            },
            {
              name: "recoverable",
              description: "true|false — whether the failure is retryable.",
              type: "string",
              required: false,
            },
          ],
        },
      );
    }

    // Host-facing operations — invoked by the runner via `connector_mutate`
    // (Phase 2 of flow-connector extraction). These are NOT exposed to the
    // agent's tool surface; they're the same instance methods the typed flow
    // commands call today, dispatched generically through `executeOp`.
    //
    // Visibility: include them only on read-write handles, matching the
    // existing host-vs-agent boundary.
    if (handle.access === "read-write") {
      ops.push(
        {
          name: "applyApproval",
          description: "Host: record an approval decision on a node awaiting approval.",
          accessLevel: "write",
          args: [
            { name: "nodeId", description: "Node ID", type: "string", required: true },
            {
              name: "decision",
              description: "approved | rejected",
              type: "string",
              required: true,
            },
            {
              name: "feedback",
              description:
                "Feedback shown alongside the decision. Optional, except when approving a failed check: that is an override and requires a reason.",
              type: "string",
              required: false,
            },
            {
              name: "decidedBy",
              description: "User id of the decider.",
              type: "string",
              required: true,
            },
          ],
        },
        {
          name: "applyInput",
          description: "Host: provide user input for a node awaiting input.",
          accessLevel: "write",
          args: [
            { name: "nodeId", description: "Node ID", type: "string", required: true },
            {
              name: "response",
              description: "Input response (shape matches the InputSchema the agent requested).",
              type: "json",
              required: true,
            },
            {
              name: "providedBy",
              description: "User id of the input provider.",
              type: "string",
              required: true,
            },
          ],
        },
        {
          name: "cancel",
          description: "Host: cancel the active flow run.",
          accessLevel: "write",
          args: [],
        },
        {
          name: "setAutonomousMode",
          description: "Host: toggle autonomous mode on the running flow.",
          accessLevel: "write",
          args: [
            {
              name: "enabled",
              description: "true|false — whether autonomous mode is on.",
              type: "boolean",
              required: true,
            },
          ],
        },
        {
          name: "retryNode",
          description: "Host: transition a recoverable failed node back to available.",
          accessLevel: "write",
          args: [
            { name: "nodeId", description: "Node ID", type: "string", required: true },
            {
              name: "requestedBy",
              description: "User id of the retry requester (informational).",
              type: "string",
              required: true,
            },
          ],
        },
        {
          name: "hydrate",
          description: "Host: replace the current execution snapshot with a host-provided one.",
          accessLevel: "write",
          args: [
            {
              name: "state",
              description: "FlowExecution snapshot to hydrate into the adapter.",
              type: "json",
              required: true,
            },
          ],
        },
        {
          name: "start",
          description:
            "Host: seed a fresh flow execution. Replaces the adapter's current flow + execution state in-place.",
          accessLevel: "write",
          args: [
            {
              name: "flow",
              description: "FlowDefinition to execute.",
              type: "json",
              required: true,
            },
            {
              name: "seed",
              description:
                "Seed object: { runId, startedBy, autonomousMode? } used to construct the initial FlowExecution.",
              type: "json",
              required: true,
            },
          ],
        },
      );
    }

    return ops;
  }

  async executeOp(
    handle: ConnectorHandle,
    operation: string,
    args: Record<string, unknown>,
  ): Promise<string> {
    const s = this.s(handle);
    const agentTarget = [...s.inlineOperationTargets.values()].find((target) =>
      target.isAgentTurnActive(),
    );
    if (agentTarget && AGENT_INLINE_OPERATIONS.has(operation)) {
      return agentTarget.executeAgentOp(operation, args);
    }
    if (operation === "applyApproval" || operation === "applyInput") {
      const parentNodeId = String(args.nodeId ?? "");
      const target = s.inlineOperationTargets.get(parentNodeId);
      if (target) {
        const projection =
          operation === "applyApproval"
            ? await target.applyParentApproval(args)
            : await target.applyParentInput(args);
        this.projectInlineInteraction(handle, parentNodeId, projection);
        return JSON.stringify(s.execution);
      }
    }
    if (operation === "cancel") {
      await this.cancelInlineTargets(s);
      this.cancel(handle);
      return JSON.stringify(s.execution);
    }
    if (operation === "hydrate" || operation === "start") {
      await this.cancelInlineTargets(s);
    }

    switch (operation) {
      case "get_available":
        return this.opGetAvailable(s);
      case "get_state":
        return JSON.stringify(s.execution);
      case "get_globals":
        return JSON.stringify(flowDefaults(s.flowDef));
      case "build_handoff":
        return this.buildHandoff(s, String(args.nodeId ?? ""));
      case "validate_node":
        return this.opValidateNode(s, args);
      case "start_node":
        return this.opStartNode(handle, args);
      case "request_approval":
        return this.opRequestApproval(handle, args);
      case "request_input":
        return this.opRequestInput(handle, args);
      case "complete_node":
        return this.opCompleteNode(handle, args);
      case "skip_node":
        return this.opSkipNode(handle, args);
      case "fail_node":
        return this.opFailNode(handle, args);

      // ── Host-facing operations (Phase 2 of flow-connector extraction) ──
      // These dispatch to the same instance methods the typed flow commands
      // call today. JSON-stringify the resulting FlowExecution (or `""` for
      // void returns) so the wire surface is uniform across ops.

      case "applyApproval":
        return this.opApplyApproval(handle, s, args);
      case "applyInput":
        return this.opApplyInput(handle, s, args);
      case "cancel":
        throw new Error("FlowAdapter: cancel routing invariant violated");
      case "setAutonomousMode":
        return this.opSetAutonomousMode(handle, s, args);
      case "retryNode":
        return this.opRetryNode(handle, s, args);
      case "hydrate":
        return this.opHydrate(handle, s, args);
      case "start":
        return this.opStart(handle, s, args);

      default:
        throw new Error(`FlowAdapter: Unknown operation: ${operation}`);
    }
  }

  // ── executeOp branch handlers (private dispatch helpers) ─────────────────

  private opGetAvailable(s: FlowAdapterState): string {
    const agentNodeIds = new Set(s.flowDef.nodes.filter(isAgentNode).map((node) => node.id));
    const nodes: NodeExecution[] = Object.values(s.execution.nodes);
    const available = nodes
      .filter((n) => agentNodeIds.has(n.id) && n.status === "available")
      .map((n) => n.id);
    return JSON.stringify(available);
  }

  private opValidateNode(s: FlowAdapterState, args: Record<string, unknown>): string {
    const nodeId = String(args.nodeId ?? "");
    const node = s.execution.nodes[nodeId];
    if (!node) return JSON.stringify({ valid: false, reason: "Node not found" });
    if (!isAgentNode(findNode(s.flowDef, nodeId))) {
      return JSON.stringify({ valid: false, reason: "Node is not an active agent node" });
    }
    const predsMet = flowPredecessors(s.flowDef, nodeId).every((pid) => {
      const predStatus = s.execution.nodes[pid]?.status ?? "not_started";
      return predStatus === "complete" || predStatus === "skipped";
    });
    return JSON.stringify({
      valid: node.status === "available",
      status: node.status,
      predecessorsReady: predsMet,
      failedPreconditions: node.failedPreconditions ?? [],
    });
  }

  private opStartNode(handle: ConnectorHandle, args: Record<string, unknown>): string {
    const nodeId = String(args.nodeId ?? "");
    return this.doStartNode(handle, nodeId);
  }

  private opRequestApproval(handle: ConnectorHandle, args: Record<string, unknown>): string {
    const nodeId = String(args.nodeId ?? "");
    const summary = String(args.summary ?? "");
    const artifacts = safeParseArtifacts(args.artifacts);
    return this.doRequestApproval(handle, nodeId, summary, artifacts);
  }

  private opRequestInput(handle: ConnectorHandle, args: Record<string, unknown>): string {
    const nodeId = String(args.nodeId ?? "");
    const prompt = String(args.prompt ?? "");
    const coerced = coerceInputSchema(args.schema);
    // Storing a gate no host can render parks the run on a question no human can
    // answer; refusing leaves the agent able to correct it and call again.
    if (!coerced.ok) {
      throw new Error(
        `FlowAdapter: request_input schema for node ${nodeId} cannot be rendered as an answerable control, so nothing was stored. ${coerced.issues.join("; ")}. Fix the schema and call request_input again.`,
      );
    }
    return this.doRequestInput(handle, nodeId, prompt, coerced.schema);
  }

  private opCompleteNode(handle: ConnectorHandle, args: Record<string, unknown>): string {
    const nodeId = String(args.nodeId ?? "");
    const parsedFields = args.fields === undefined ? { issues: [] } : safeParseFields(args.fields);
    const output: NodeOutput = {
      summary: String(args.summary ?? "Completed"),
      artifacts: safeParseArtifacts(args.artifacts),
      concerns: safeParseStringArray(args.concerns),
      ...(parsedFields.fields ? { fields: parsedFields.fields } : {}),
    };
    return this.doCompleteNode(handle, nodeId, output, parsedFields.issues);
  }

  private opSkipNode(handle: ConnectorHandle, args: Record<string, unknown>): string {
    const nodeId = String(args.nodeId ?? "");
    return this.doSkipNode(handle, nodeId);
  }

  private opFailNode(handle: ConnectorHandle, args: Record<string, unknown>): string {
    const nodeId = String(args.nodeId ?? "");
    const message = String(args.message ?? "failed");
    const recoverable =
      args.recoverable === true || args.recoverable === "true" || args.recoverable === "yes";
    return this.doFailNode(handle, nodeId, message, recoverable);
  }

  private opApplyApproval(
    handle: ConnectorHandle,
    s: FlowAdapterState,
    args: Record<string, unknown>,
  ): string {
    const nodeId = String(args.nodeId ?? "");
    const decision = args.decision === "rejected" ? "rejected" : "approved";
    const feedback =
      typeof args.feedback === "string" && args.feedback.length > 0 ? args.feedback : undefined;
    const decidedBy = String(args.decidedBy ?? "");
    this.applyApproval(handle, nodeId, decision, feedback, decidedBy);
    return JSON.stringify(s.execution);
  }

  private opApplyInput(
    handle: ConnectorHandle,
    s: FlowAdapterState,
    args: Record<string, unknown>,
  ): string {
    const nodeId = String(args.nodeId ?? "");
    const response = args.response;
    const providedBy = String(args.providedBy ?? "");
    this.applyInput(handle, nodeId, response, providedBy);
    return JSON.stringify(s.execution);
  }

  private opSetAutonomousMode(
    handle: ConnectorHandle,
    s: FlowAdapterState,
    args: Record<string, unknown>,
  ): string {
    const enabled = args.enabled === true || args.enabled === "true";
    this.setAutonomousMode(handle, enabled);
    return JSON.stringify(s.execution);
  }

  private opRetryNode(
    handle: ConnectorHandle,
    s: FlowAdapterState,
    args: Record<string, unknown>,
  ): string {
    const nodeId = String(args.nodeId ?? "");
    // `requestedBy` is informational only — the adapter doesn't record it
    // (consistent with how the typed `retry_flow_node` handler behaves
    // today; the field is logged at the runner, not in the snapshot).
    void String(args.requestedBy ?? "");
    this.retryNode(handle, nodeId);
    return JSON.stringify(s.execution);
  }

  private opHydrate(
    handle: ConnectorHandle,
    s: FlowAdapterState,
    args: Record<string, unknown>,
  ): string {
    const state = args.state as FlowExecution | undefined;
    if (!state || typeof state !== "object") {
      throw new Error("FlowAdapter: hydrate requires an args.state FlowExecution snapshot");
    }
    this.hydrate(handle, state);
    return JSON.stringify(s.execution);
  }

  private opStart(
    handle: ConnectorHandle,
    s: FlowAdapterState,
    args: Record<string, unknown>,
  ): string {
    const authoredFlow = args.flow;
    if (!authoredFlow || typeof authoredFlow !== "object") {
      throw new Error("FlowAdapter: start requires args.flow (FlowDefinition)");
    }
    const flow = normalizeRuntimeFlow(authoredFlow);
    const seed = (args.seed ?? {}) as {
      runId?: string;
      startedBy?: string;
      autonomousMode?: boolean;
    };
    if (!seed.runId || !seed.startedBy) {
      throw new Error("FlowAdapter: start requires args.seed.runId + args.seed.startedBy");
    }
    // Seed a fresh execution in-place. Same flow used by `connect()` —
    // we reuse `buildInitialExecution` + `recompute` so the adapter
    // produces an identical initial FlowExecution to a fresh connect.
    s.flowDef = flow;
    void this.cancelSubprompts(s);
    s.executionGeneration += 1;
    s.execution = buildInitialExecution(flow, {
      runId: seed.runId,
      startedBy: seed.startedBy,
      autonomousMode: seed.autonomousMode ?? false,
    });
    s.execution = this.recompute(s);
    if (s.onChange) s.onChange(s.execution);
    return JSON.stringify(s.execution);
  }

  // ── Agent-side mutations (tool calls) ─────────────────────────────────────

  private doStartNode(handle: ConnectorHandle, nodeId: string): string {
    return this.mutate(handle, (s) => {
      const definition = findNode(s.flowDef, nodeId);
      assertLegal(isAgentNode(definition), `node is not an active agent node: ${nodeId}`);
      const node = s.execution.nodes[nodeId];
      assertLegal(node, `node not in execution: ${nodeId}`);
      assertLegal(node.status === "available", `cannot start ${nodeId}: status is ${node.status}`);
      // Auditability only: an agent node's output is agent-obtained by kind, so
      // nothing reads this back. Never let a resolution failure block the start —
      // the binding values reach the agent through `build_handoff`, not here.
      let inputOrigins: Record<string, BindingOrigin[]> | undefined;
      try {
        inputOrigins = resolveNodeBindings(
          s.flowDef,
          definition,
          snapshotMap(s.execution),
          s.flowInputOrigins,
        ).origins;
      } catch {
        inputOrigins = undefined;
      }
      s.execution.nodes[nodeId] = {
        ...node,
        status: "running",
        startedAt: now(),
        inputOrigins,
      };
      return JSON.stringify({ ok: true, status: "running" });
    });
  }

  private doRequestApproval(
    handle: ConnectorHandle,
    nodeId: string,
    summary: string,
    artifacts: ArtifactRef[],
  ): string {
    return this.mutate(handle, (s) => {
      const definition = findNode(s.flowDef, nodeId);
      const node = s.execution.nodes[nodeId];
      assertLegal(node, `node not in execution: ${nodeId}`);
      const rejectedGateRerequest =
        isGateNode(definition) &&
        node.status === "awaiting_approval" &&
        node.approval?.decision === "rejected";
      assertLegal(
        isAgentNode(definition) || rejectedGateRerequest,
        `node is not an active agent node or rejected gate: ${nodeId}`,
      );
      assertLegal(
        (isAgentNode(definition) && node.status === "running") || rejectedGateRerequest,
        `request_approval requires a running agent node or rejected gate, got ${node.status}`,
      );
      const approval: Approval = { requestedAt: now(), summary };
      const history = [...node.approvalHistory, approval];
      s.execution.nodes[nodeId] = {
        ...node,
        status: "awaiting_approval",
        approval,
        approvalHistory: history,
        // Stash output so the UI can render the approval card with artifacts
        // before the node reaches `complete`.
        output: { summary, artifacts },
      };
      return JSON.stringify({ ok: true, status: "awaiting_approval" });
    });
  }

  private doRequestInput(
    handle: ConnectorHandle,
    nodeId: string,
    prompt: string,
    schema: InputSchema,
  ): string {
    return this.mutate(handle, (s) => {
      assertLegal(
        isAgentNode(findNode(s.flowDef, nodeId)),
        `node is not an active agent node: ${nodeId}`,
      );
      const node = s.execution.nodes[nodeId];
      assertLegal(node, `node not in execution: ${nodeId}`);
      assertLegal(
        node.status === "available" ||
          node.status === "running" ||
          node.status === "awaiting_approval",
        `request_input not legal from ${node.status}`,
      );
      const input: InputRequest = { requestedAt: now(), prompt, schema };
      s.execution.nodes[nodeId] = {
        ...node,
        // Record the prior status so `applyInput` can restore it.
        // We encode this via `inputHistory` — the latest entry's metadata
        // includes the resume status in a convention-only field.
        status: "awaiting_input",
        input,
        inputHistory: [
          ...node.inputHistory,
          { ...input, response: { _resumeStatus: node.status } },
        ],
      };
      return JSON.stringify({ ok: true, status: "awaiting_input" });
    });
  }

  private doCompleteNode(
    handle: ConnectorHandle,
    nodeId: string,
    output: NodeOutput,
    fieldIssues: string[] = [],
  ): string {
    const state = this.s(handle);
    assertLegal(
      isAgentNode(findNode(state.flowDef, nodeId)),
      `node is not an active agent node: ${nodeId}`,
    );
    const node = state.execution.nodes[nodeId];
    assertLegal(node, `node not in execution: ${nodeId}`);

    const autonomous = state.execution.autonomousMode;
    const mandatory = requiresAgentApproval(state.flowDef, nodeId);
    const approvedPrior =
      node.status === "awaiting_approval" && node.approval?.decision === "approved";
    const runningAutonomous = node.status === "running" && autonomous && !mandatory;
    assertLegal(
      approvedPrior || runningAutonomous,
      mandatory && node.status === "running"
        ? `complete_node blocked: node ${nodeId} is a mandatory approval gate — call request_approval and wait for an approved decision (autonomous mode cannot skip it)`
        : `complete_node requires awaiting_approval+approved OR running+autonomous; node is ${node.status}${
            node.approval?.decision ? ` with approval ${node.approval.decision}` : ""
          }, autonomous=${autonomous}`,
    );

    const validation =
      fieldIssues.length > 0 && getConsumedOutputSchema(state.flowDef, nodeId)
        ? { valid: false, issues: fieldIssues }
        : validateConsumedNodeOutput(state.flowDef, nodeId, output.fields);
    if (!validation.valid) {
      const issueText = validation.issues.join("; ");
      if (!node.outputCorrection) {
        const correction: NodeOutputCorrection = {
          requestedAt: now(),
          issues: validation.issues,
        };
        // Finish recompute/onChange/stimulus publication before surfacing the tool error.
        this.mutate(handle, (current) => {
          const execution = current.execution.nodes[nodeId];
          assertLegal(execution, `node not in execution: ${nodeId}`);
          current.execution.nodes[nodeId] = { ...execution, outputCorrection: correction };
        });
        throw new Error(
          `FlowAdapter: output for node ${nodeId} does not match its consumed schema. Submit one corrected complete_node call. ${issueText}`,
        );
      }

      this.mutate(handle, (current) => {
        const execution = current.execution.nodes[nodeId];
        assertLegal(execution, `node not in execution: ${nodeId}`);
        const completedAt = now();
        const error: NodeError = {
          message: `Node ${nodeId} output failed schema validation after one correction: ${issueText}`,
          recoverable: false,
          at: completedAt,
        };
        // The correction stayed inside this attempt (no new entry on the
        // first mismatch) — this is the attempt's one terminal entry.
        const outputHistory = execution.startedAt
          ? appendAttempt(
              execution.outputHistory,
              "failed",
              execution.startedAt,
              completedAt,
              execution.output,
              error,
            )
          : execution.outputHistory;
        current.execution.nodes[nodeId] = {
          ...execution,
          status: resolveTerminalFailureStatus(findNode(current.flowDef, nodeId)),
          completedAt,
          error,
          errorHistory: [...execution.errorHistory, error],
          outputHistory,
        };
      });
      throw new Error(
        `FlowAdapter: node ${nodeId} failed after one output correction. ${issueText}`,
      );
    }

    return this.mutate(handle, (current) => {
      const execution = current.execution.nodes[nodeId];
      assertLegal(execution, `node not in execution: ${nodeId}`);
      const completedAt = now();
      const outputHistory = execution.startedAt
        ? appendAttempt(
            execution.outputHistory,
            "complete",
            execution.startedAt,
            completedAt,
            output,
            undefined,
          )
        : execution.outputHistory;
      current.execution.nodes[nodeId] = {
        ...execution,
        status: "complete",
        completedAt,
        output,
        outputHistory,
      };
      return JSON.stringify({ ok: true, status: "complete" });
    });
  }

  private doSkipNode(handle: ConnectorHandle, nodeId: string): string {
    return this.mutate(handle, (s) => {
      assertLegal(
        isAgentNode(findNode(s.flowDef, nodeId)),
        `node is not an active agent node: ${nodeId}`,
      );
      const node = s.execution.nodes[nodeId];
      assertLegal(node, `node not in execution: ${nodeId}`);
      // Mandatory wins over optional: a hard gate must go through
      // request_approval → applyApproval — it can never be skipped.
      assertLegal(
        !requiresAgentApproval(s.flowDef, nodeId),
        `cannot skip node ${nodeId}: it is a mandatory approval gate — call request_approval and wait for an approved decision`,
      );
      assertLegal(isOptional(s.flowDef, nodeId), `cannot skip required node ${nodeId}`);
      const completedAt = now();
      // Only an actually-started node opens an attempt — skipping a node
      // that never ran (still `available`) has nothing to record.
      const outputHistory = node.startedAt
        ? appendAttempt(
            node.outputHistory,
            "skipped",
            node.startedAt,
            completedAt,
            node.output,
            undefined,
          )
        : node.outputHistory;
      s.execution.nodes[nodeId] = {
        ...node,
        status: "skipped",
        completedAt,
        outputHistory,
      };
      return JSON.stringify({ ok: true, status: "skipped" });
    });
  }

  private doFailNode(
    handle: ConnectorHandle,
    nodeId: string,
    message: string,
    recoverable: boolean,
  ): string {
    return this.mutate(handle, (s) => {
      assertLegal(
        isAgentNode(findNode(s.flowDef, nodeId)),
        `node is not an active agent node: ${nodeId}`,
      );
      const node = s.execution.nodes[nodeId];
      assertLegal(node, `node not in execution: ${nodeId}`);
      const error: NodeError = { message, recoverable, at: now() };
      if (recoverable) {
        // Transition back to `available` for the agent to retry within
        // the same turn; keep the failure in history so the next turn
        // sees the prior error context. This is the agent's own recovery
        // path (full agency, unbounded) — distinct from, and outside, the
        // automatic retry budget. It still closes out an attempt — the node
        // genuinely started and genuinely failed, and the next `start_node`
        // unconditionally overwrites `startedAt`, so this is the only chance
        // to capture this attempt's own timing — but the entry is marked
        // `selfRetry` so `countFailedAttempts` (the automatic-retry budget)
        // never counts it. `attempt` numbering still includes it: the
        // numbering tracks every actual start, not just automatic ones.
        const completedAt = error.at;
        const outputHistory = node.startedAt
          ? appendAttempt(
              node.outputHistory,
              "failed",
              node.startedAt,
              completedAt,
              node.output,
              error,
              { selfRetry: true },
            )
          : node.outputHistory;
        s.execution.nodes[nodeId] = {
          ...node,
          status: "available",
          error,
          errorHistory: [...node.errorHistory, error],
          outputHistory,
        };
      } else {
        const completedAt = now();
        const outputHistory = node.startedAt
          ? appendAttempt(
              node.outputHistory,
              "failed",
              node.startedAt,
              completedAt,
              node.output,
              error,
            )
          : node.outputHistory;
        s.execution.nodes[nodeId] = {
          ...node,
          status: resolveTerminalFailureStatus(findNode(s.flowDef, nodeId)),
          completedAt,
          error,
          errorHistory: [...node.errorHistory, error],
          outputHistory,
        };
      }
      // Reads back the status actually written — an optional node reports the
      // `skipped` it became, never the `failed` the agent asked for.
      return JSON.stringify({ ok: true, status: s.execution.nodes[nodeId].status });
    });
  }

  // ── Host-side mutations (not exposed as agent tools) ─────────────────────

  /**
   * Record an approval decision on a node currently `awaiting_approval`.
   * A first-class gate completes directly when approved and remains parked when rejected.
   * Agent-node approvals keep their existing resume behavior. Approving an escalated check
   * is a human override: it demands a reason and completes the node so the run continues,
   * but records only the decision — the attempt's evidence stays `passed: false`.
   * Rejecting a check is terminal.
   */
  applyApproval(
    handle: ConnectorHandle,
    nodeId: string,
    decision: "approved" | "rejected",
    feedback: string | undefined,
    decidedBy: string,
  ): void {
    this.mutate(handle, (s) => {
      const definition = findNode(s.flowDef, nodeId);
      const node = s.execution.nodes[nodeId];
      assertLegal(node, `applyApproval: unknown node ${nodeId}`);
      assertLegal(
        node.status === "awaiting_approval",
        `applyApproval: node ${nodeId} is ${node.status}, not awaiting_approval`,
      );
      if (isGateNode(definition)) {
        assertLegal(
          node.approval?.decidedAt == null && node.approval?.decision == null,
          `applyApproval: gate ${nodeId} approval attempt is already decided; request approval again before applying another decision`,
        );
      }
      if (isCheckNode(definition) && decision === "approved") {
        // An override outranks a failed observation, so it may never be anonymous:
        // the reason is the only record of why the run was allowed to continue.
        assertLegal(
          feedback != null && feedback.trim().length > 0,
          `applyApproval: overriding failed check ${nodeId} requires a reason — approving records a human decision over the failure, it does not make the check pass`,
        );
        // Completing the node exposes the failed attempt's output to
        // `prepareExecutionSnapshot`, which validates every `complete` node's
        // output against the consumed schema and throws for the whole execution.
        // Refuse here instead: a refused override leaves the run parked and
        // explainable; an accepted one would make it unhydratable on resume.
        const consumed = validateConsumedNodeOutput(s.flowDef, nodeId, node.output?.fields);
        assertLegal(
          consumed.valid,
          `applyApproval: cannot override failed check ${nodeId} — downstream nodes consume its declared output and the failed attempt does not satisfy it (${consumed.issues.join("; ")}); reject the check instead`,
        );
      }
      const base = node.approval ?? { requestedAt: now(), summary: "" };
      const approval: Approval = {
        ...base,
        decidedAt: now(),
        decidedBy,
        decision,
        feedback,
      };
      // Replace the tail of history with the decided version so the
      // pointer and the history element stay in sync.
      const history = [...node.approvalHistory];
      if (history.length > 0) history[history.length - 1] = approval;
      else history.push(approval);

      if (isGateNode(definition) && decision === "approved") {
        s.execution.nodes[nodeId] = {
          ...node,
          status: "complete",
          completedAt: now(),
          approval,
          approvalHistory: history,
        };
      } else if (isGateNode(definition)) {
        s.execution.nodes[nodeId] = {
          ...node,
          status: "awaiting_approval",
          approval,
          approvalHistory: history,
        };
      } else if (isCheckNode(definition) && decision === "approved") {
        // A human override completes the node so dependents unblock, but it decides
        // nothing about what was observed: `errorHistory` and `outputHistory` keep the
        // failure, and its evidence stays `passed: false`.
        s.execution.nodes[nodeId] = {
          ...node,
          status: "complete",
          completedAt: now(),
          error: undefined,
          approval,
          approvalHistory: history,
        };
      } else if (isCheckNode(definition)) {
        // Reject is terminal: a check has no conversation to resume and no passing
        // state to fall back into, so "the run gives up" means `failed`, never
        // `available`.
        s.execution.nodes[nodeId] = {
          ...node,
          status: resolveTerminalFailureStatus(definition),
          completedAt: now(),
          approval,
          approvalHistory: history,
        };
      } else if (decision === "approved") {
        // Non-gate (agent-node HITL) approval deliberately stays at
        // `awaiting_approval` rather than resuming `running`: `doCompleteNode`'s
        // `approvedPrior` check requires exactly that status (a mandatory-approval
        // node cannot fall back to the `runningAutonomous` branch), so the
        // agent's next legal call is `complete_node`, not more work. See
        // `isDeadlineEligible` for how `control.timeoutSec` still re-evaluates
        // this window without needing `status === "running"`.
        s.execution.nodes[nodeId] = { ...node, approval, approvalHistory: history };
      } else {
        s.execution.nodes[nodeId] = {
          ...node,
          status: "available",
          approval,
          approvalHistory: history,
        };
      }
      return undefined;
    });
  }

  /**
   * Record a user-provided input on a node currently `awaiting_input`.
   * Transitions back to the status the node had before requesting input.
   */
  applyInput(handle: ConnectorHandle, nodeId: string, response: unknown, providedBy: string): void {
    this.mutate(handle, (s) => {
      const node = s.execution.nodes[nodeId];
      assertLegal(node, `applyInput: unknown node ${nodeId}`);
      assertLegal(
        node.status === "awaiting_input",
        `applyInput: node ${nodeId} is ${node.status}, not awaiting_input`,
      );

      // Recover the pre-input status from the last history entry's
      // `_resumeStatus` marker (see doRequestInput).
      const last = node.inputHistory[node.inputHistory.length - 1];
      const resumeStatus =
        (last?.response as { _resumeStatus?: NodeStatus } | undefined)?._resumeStatus ?? "running";

      const updated: InputRequest = {
        ...(node.input ?? { requestedAt: now(), prompt: "", schema: { kind: "text" } }),
        providedAt: now(),
        providedBy,
        response,
      };

      const history = [...node.inputHistory];
      if (history.length > 0) history[history.length - 1] = updated;
      else history.push(updated);

      s.execution.nodes[nodeId] = {
        ...node,
        status: resumeStatus,
        input: updated,
        inputHistory: history,
      };
      return undefined;
    });
  }

  /**
   * Transition a recoverable failed node back to `available` for retry.
   * Preserves the failure in `errorHistory` so the next turn's agent can
   * see the prior failure context.
   *
   * `failed` is the only status accepted; a `skipped` node is refused
   * deliberately, because a skip has already released its dependents and this
   * operation owns no cascade machinery to unwind the ones that ran without
   * the producer's output. Reasoning in
   * `_devlog/entries/2026-08-15-optional-node-skip-semantics.md`.
   */
  retryNode(handle: ConnectorHandle, nodeId: string): void {
    this.mutate(handle, (s) => {
      const node = s.execution.nodes[nodeId];
      assertLegal(node, `retryNode: unknown node ${nodeId}`);
      assertLegal(
        node.status !== "skipped",
        `retryNode: node ${nodeId} was skipped, and a skip is not recoverable — it already released its dependents, which may have run without this node's output`,
      );
      assertLegal(
        node.status === "failed" && node.error?.recoverable === true,
        `retryNode: node ${nodeId} is not a recoverable failure (status=${node.status}, recoverable=${node.error?.recoverable})`,
      );
      s.execution.nodes[nodeId] = {
        ...node,
        status: "available",
        error: undefined,
      };
      return undefined;
    });
  }

  /** Toggle autonomous mode at the flow-execution level. */
  setAutonomousMode(handle: ConnectorHandle, enabled: boolean): void {
    this.mutate(handle, (s) => {
      s.execution = { ...s.execution, autonomousMode: enabled };
      return undefined;
    });
  }

  /**
   * Cancel the flow run. Status transitions to `cancelled`; dependents stay
   * put. Bumps `executionGeneration` — the sole late-settlement rejection
   * mechanism every in-flight await (function executor, sub-flow child)
   * checks after resolving, so a result that arrives after cancellation can
   * never write state.
   */
  cancel(handle: ConnectorHandle): void {
    this.mutate(handle, (s) => {
      void this.cancelSubprompts(s);
      s.execution = { ...s.execution, status: "cancelled" };
      s.executionGeneration += 1;
      return undefined;
    });
  }

  /** Read-only access to the current FlowExecution snapshot. */
  getExecution(handle: ConnectorHandle): FlowExecution {
    return this.s(handle).execution;
  }

  /** @internal Whether a nested inline descendant owns the current agent turn. */
  hasActiveInlineAgentTurn(handle: ConnectorHandle): boolean {
    return [...this.s(handle).inlineOperationTargets.values()].some((target) =>
      target.isAgentTurnActive(),
    );
  }

  /** Snapshot currently visible through agent-facing tools, including a live inline child. */
  getAgentExecution(handle: ConnectorHandle): FlowExecution {
    const state = this.s(handle);
    return (
      [...state.inlineOperationTargets.values()]
        .find((target) => target.isAgentTurnActive())
        ?.getExecution() ?? state.execution
    );
  }

  /** Agent node IDs whose approval policy remains mandatory in autonomous mode. */
  getMandatoryAgentApprovalNodeIds(handle: ConnectorHandle): string[] {
    const state = this.s(handle);
    const target = [...state.inlineOperationTargets.values()].find((candidate) =>
      candidate.isAgentTurnActive(),
    );
    if (target) return target.getMandatoryAgentApprovalNodeIds();
    return state.flowDef.nodes.filter(nodeRequiresApproval).map((n) => n.id);
  }

  /** @deprecated Use {@link getMandatoryAgentApprovalNodeIds}. */
  getMandatoryGates(handle: ConnectorHandle): string[] {
    return this.getMandatoryAgentApprovalNodeIds(handle);
  }

  /**
   * Decide whether the current state and stimulus require the session agent's conversation.
   * Runtime-owned gates stay dark while pending; a rejected gate is the exception because the
   * rejection starts the revision conversation promised by the gate lifecycle.
   */
  shouldDriveAgentTurn(handle: ConnectorHandle, stimulus: TurnStimulus): boolean {
    const state = this.s(handle);
    const execution = state.execution;
    if (execution.done || execution.status === "failed" || execution.status === "cancelled") {
      return false;
    }
    if (stimulus.kind === "user_message") return true;
    for (const node of state.flowDef.nodes) {
      const current = execution.nodes[node.id];
      if (
        isGateNode(node) &&
        current?.status === "awaiting_approval" &&
        current.approval?.decision === "rejected"
      ) {
        return true;
      }
    }

    for (const node of state.flowDef.nodes) {
      if (!isAgentNode(node)) continue;
      const current = execution.nodes[node.id];
      if (!current) continue;
      if (current.status === "available" || current.status === "running") return true;
      if (current.status === "awaiting_approval" && current.approval?.decision === "approved") {
        return true;
      }
    }
    return false;
  }

  /**
   * Drain runtime-owned nodes in definition order until only agent work or a human decision
   * remains. Routers select branches, function nodes use NodeExecutor, gates park, and sub-flows
   * delegate to the host.
   */
  async drainRuntimeNodes(handle: ConnectorHandle): Promise<FlowExecution> {
    return this.drainAvailableRuntimeNodes(handle, true);
  }

  /**
   * Run every currently available function and check node through the
   * configured NodeExecutor. Newly-unblocked function/check nodes are
   * drained in definition order before returning control to the runner's
   * agent-turn decision. Routers and gates are excluded — see
   * {@link drainRuntimeNodes} for the full drain.
   */
  async executeAvailableFunctionNodes(handle: ConnectorHandle): Promise<FlowExecution> {
    return this.drainAvailableRuntimeNodes(handle, false);
  }

  private async drainAvailableRuntimeNodes(
    handle: ConnectorHandle,
    includeRoutersAndGates: boolean,
  ): Promise<FlowExecution> {
    const advancedSubFlows = new Set<string>();
    let abortedAttempt: Promise<NodeExecutorResult> | undefined;
    while (true) {
      const state = this.s(handle);
      if (state.subpromptCleanup.size > 0) await Promise.allSettled([...state.subpromptCleanup]);
      if (state.execution.status === "cancelled" || state.execution.status === "failed") break;
      if (includeRoutersAndGates && this.settleAvailableRouters(handle)) continue;
      const node = state.flowDef.nodes.find((candidate) => {
        const status = state.execution.nodes[candidate.id]?.status;
        if (
          isSubFlowNode(candidate) &&
          !advancedSubFlows.has(candidate.id) &&
          (status === "available" || status === "running")
        ) {
          return true;
        }
        return (
          (isFunctionNode(candidate) ||
            isCheckNode(candidate) ||
            isSubpromptNode(candidate) ||
            (includeRoutersAndGates && isGateNode(candidate))) &&
          status === "available"
        );
      });
      if (!node) break;

      if (isGateNode(node)) {
        // Records no `inputOrigins` on purpose: a gate resolves no bindings and
        // writes no output, so nothing downstream can bind to it.
        this.mutate(
          handle,
          (current) => {
            const execution = current.execution.nodes[node.id];
            assertLegal(execution, `node not in execution: ${node.id}`);
            assertLegal(
              execution.status === "available",
              `cannot park gate node ${node.id}: status is ${execution.status}`,
            );
            const approval: Approval = { requestedAt: now(), summary: node.run.prompt };
            current.execution.nodes[node.id] = {
              ...execution,
              status: "awaiting_approval",
              approval,
              approvalHistory: [...execution.approvalHistory, approval],
            };
          },
          false,
        );
        continue;
      }

      if (isSubFlowNode(node)) {
        advancedSubFlows.add(node.id);
        const execution = state.execution.nodes[node.id];
        assertLegal(execution, `node not in execution: ${node.id}`);
        if (execution.status === "available") {
          this.mutate(
            handle,
            (current) => {
              const currentExecution = current.execution.nodes[node.id];
              assertLegal(currentExecution, `node not in execution: ${node.id}`);
              assertLegal(
                currentExecution.status === "available",
                `cannot execute sub-flow node ${node.id}: status is ${currentExecution.status}`,
              );
              current.execution.nodes[node.id] = {
                ...currentExecution,
                status: "running",
                startedAt: now(),
              };
            },
            false,
          );
        }

        const executionGeneration = state.executionGeneration;
        const timeoutSec = node.control?.timeoutSec;
        const runningExecution = state.execution.nodes[node.id];
        const remainingMs = runningExecution
          ? remainingActiveExecutionMs(runningExecution, timeoutSec, Date.now())
          : undefined;
        let result: Awaited<ReturnType<InlineSubFlowRunner["run"]>>;
        let subFlowInputOrigins: Record<string, BindingOrigin[]> | undefined;
        try {
          const runner = state.inlineSubFlowRunner;
          if (!runner) {
            throw new Error(`no inline sub-flow runner configured for node ${node.id}`);
          }
          const resolved = resolveNodeBindings(
            state.flowDef,
            node,
            snapshotMap(state.execution),
            state.flowInputOrigins,
          );
          subFlowInputOrigins = resolved.origins;
          const runPromise = runner.run({
            node,
            flowId: node.run.flow,
            inputs: resolved.values,
            inputOrigins: {
              ...(node.run.passContext ? state.flowInputOrigins : {}),
              ...resolved.origins,
            },
            passContext: node.run.passContext ?? false,
            parentFlow: state.flowDef,
            parentExecution: state.execution,
            ancestry: state.inlineSubFlowAncestry,
            routeOperationsTo: (target) => {
              if (target) state.inlineOperationTargets.set(node.id, target);
              else state.inlineOperationTargets.delete(node.id);
            },
          });
          result =
            remainingMs !== undefined
              ? await raceWithTimeout(
                  runPromise,
                  Math.max(0, remainingMs),
                  `Sub-flow node ${node.id} timed out after ${timeoutSec}s`,
                )
              : await runPromise;
        } catch (error) {
          if (error instanceof NodeTimeoutError) {
            // Abort active work: drop (and cancel) any live inline operation
            // target so a parked child doesn't linger past the timeout. The
            // real `runPromise` is not cancelled (no abort signal on this
            // seam) — its late settlement is swallowed by raceWithTimeout,
            // and the executionGeneration check below rejects it a second
            // time if it races back in before this loop notices.
            const target = state.inlineOperationTargets.get(node.id);
            if (target) {
              state.inlineOperationTargets.delete(node.id);
              void target.cancel().catch(() => {});
            }
            result = {
              status: "failed",
              error: { message: error.message, recoverable: false, at: now() },
            };
          } else {
            result = {
              status: "failed",
              error: {
                message: `Sub-flow node ${node.id} failed: ${error instanceof Error ? error.message : String(error)}`,
                recoverable: false,
                at: now(),
              },
            };
          }
        }

        if (this.s(handle).executionGeneration !== executionGeneration) break;

        if (result.status === "paused") {
          this.projectInlineInteraction(handle, node.id, result.projection);
          continue;
        }
        let subFlowRetrying = false;
        this.mutate(
          handle,
          (current) => {
            const execution = current.execution.nodes[node.id];
            assertLegal(execution, `node not in execution: ${node.id}`);
            const completedAt = now();
            if (result.status === "complete") {
              const outputHistory = appendAttempt(
                execution.outputHistory,
                "complete",
                execution.startedAt ?? completedAt,
                completedAt,
                result.output,
                undefined,
              );
              current.execution.nodes[node.id] = {
                ...execution,
                status: "complete",
                completedAt,
                output: result.output,
                outputHistory,
                inputOrigins: subFlowInputOrigins,
              };
              return;
            }
            const { retry, patch } = this.planAutomaticRetry(
              node,
              execution,
              result.error.message,
              completedAt,
              result.output,
              result.error.at,
            );
            subFlowRetrying = retry;
            current.execution.nodes[node.id] = retry
              ? { ...execution, ...patch, status: "available", error: undefined }
              : {
                  ...execution,
                  ...patch,
                  status: resolveTerminalFailureStatus(node),
                  completedAt,
                };
          },
          false,
        );
        // A retry sets status back to "available", but `advancedSubFlows`
        // (above) would otherwise stop THIS drain from re-entering the same
        // node — unlike function nodes, which never join that set and so
        // retry within one drain for free. Nothing else re-drains: both
        // production drivers call `drainRuntimeNodes` exactly once per
        // stimulus-triggered turn, so without this the node would sit
        // `available` with no event left to pick it back up (#2196 retry
        // wedge). Dropping it out here makes sub-flow retry match the
        // function-node precedent — bounded and synchronous within this call.
        if (subFlowRetrying) advancedSubFlows.delete(node.id);
        continue;
      }

      if (isSubpromptNode(node)) {
        this.mutate(
          handle,
          (current) => {
            const execution = current.execution.nodes[node.id];
            assertLegal(execution, `node not in execution: ${node.id}`);
            assertLegal(
              execution.status === "available",
              `cannot execute subprompt node ${node.id}: status is ${execution.status}`,
            );
            current.execution.nodes[node.id] = {
              ...execution,
              status: "running",
              startedAt: now(),
            };
          },
          false,
        );

        const subpromptExecutionGeneration = state.executionGeneration;
        const subpromptTimeoutSec = node.control?.timeoutSec;
        const subpromptRunningExecution = state.execution.nodes[node.id];
        const subpromptRemainingMs = subpromptRunningExecution
          ? remainingActiveExecutionMs(subpromptRunningExecution, subpromptTimeoutSec, Date.now())
          : undefined;

        let subpromptOutput: NodeOutput | undefined;
        let subpromptInputOrigins: Record<string, BindingOrigin[]> | undefined;
        let subpromptOutcome: { kind: "success" } | { kind: "failure"; message: string };
        try {
          const runner = state.subpromptRunner;
          if (!runner) {
            throw new Error(`no SubpromptRunner configured for subprompt node ${node.id}`);
          }
          const resolved = resolveNodeBindings(
            state.flowDef,
            node,
            snapshotMap(state.execution),
            state.flowInputOrigins,
          );
          subpromptInputOrigins = resolved.origins;
          const instruction = nodeInstruction(node);
          assertLegal(instruction, `subprompt node ${node.id} has no instruction`);
          const outputSchema = node.contract?.output?.fields;
          const prompt = renderSubpromptPrompt(
            interpolateNodeInstruction(instruction, resolved.values),
            outputSchema,
          );
          const subpromptAbort = new AbortController();
          const runPromise = runner.execute({
            node,
            prompt,
            inputs: resolved.values,
            inputOrigins: resolved.origins,
            signal: subpromptAbort.signal,
          });
          const operation = { controller: subpromptAbort, promise: runPromise };
          state.subpromptOperations.set(node.id, operation);
          let result: Awaited<typeof runPromise>;
          try {
            result =
              subpromptRemainingMs !== undefined
                ? await raceWithTimeout(
                    runPromise,
                    Math.max(0, subpromptRemainingMs),
                    `Subprompt node ${node.id} timed out after ${subpromptTimeoutSec}s`,
                  )
                : await runPromise;
          } finally {
            subpromptAbort.abort();
            // Keep ownership until the runner has disposed the cancelled child.
            await runPromise.catch(() => {});
            if (state.subpromptOperations.get(node.id) === operation)
              state.subpromptOperations.delete(node.id);
          }

          subpromptOutput = subpromptNodeOutput(node, result.text);

          // Same seam every other kind's output rides — enforced only when a
          // downstream node actually consumes the declared fields. No corrective
          // reprompt exists here (a subprompt has no conversation to reprompt in),
          // so a mismatch is an ordinary node failure, not a correction request.
          const validation = validateConsumedNodeOutput(
            state.flowDef,
            node.id,
            subpromptOutput.fields,
          );
          if (!validation.valid) {
            throw new Error(
              `output does not match its consumed schema: ${validation.issues.join("; ")}`,
            );
          }
          subpromptOutcome = { kind: "success" };
        } catch (error) {
          subpromptOutcome =
            error instanceof NodeTimeoutError
              ? { kind: "failure", message: error.message }
              : {
                  kind: "failure",
                  message: `Subprompt node ${node.id} failed: ${error instanceof Error ? error.message : String(error)}`,
                };
        }

        // Late-settlement rejection, matching the function/check and sub-flow
        // seams — a hydrate/cancel/disconnect/start that landed while this await
        // was in flight invalidates whatever was just computed.
        if (this.s(handle).executionGeneration !== subpromptExecutionGeneration) break;

        if (subpromptOutcome.kind === "success") {
          this.mutate(
            handle,
            (current) => {
              const execution = current.execution.nodes[node.id];
              assertLegal(execution, `node not in execution: ${node.id}`);
              const completedAt = now();
              const outputHistory = appendAttempt(
                execution.outputHistory,
                "complete",
                execution.startedAt ?? completedAt,
                completedAt,
                subpromptOutput,
                undefined,
              );
              current.execution.nodes[node.id] = {
                ...execution,
                status: "complete",
                completedAt,
                output: subpromptOutput,
                outputHistory,
                inputOrigins: subpromptInputOrigins,
              };
            },
            false,
          );
        } else {
          const failureMessage = subpromptOutcome.message;
          this.mutate(
            handle,
            (current) => {
              const execution = current.execution.nodes[node.id];
              assertLegal(execution, `node not in execution: ${node.id}`);
              const completedAt = now();
              const { retry, patch } = this.planAutomaticRetry(
                node,
                execution,
                failureMessage,
                completedAt,
                subpromptOutput,
              );
              current.execution.nodes[node.id] = retry
                ? { ...execution, ...patch, status: "available", error: undefined }
                : {
                    ...execution,
                    ...patch,
                    status: resolveTerminalFailureStatus(node),
                    completedAt,
                  };
            },
            false,
          );
        }
        continue;
      }

      if (!isFunctionNode(node) && !isCheckNode(node)) continue;

      // Gate the next deterministic spawn on the aborted one finishing: `abort()`
      // only *starts* the kill, so without this a retry runs alongside the
      // process it replaced. Re-select afterwards — the wait is an await.
      if (abortedAttempt) {
        const settling = abortedAttempt;
        abortedAttempt = undefined;
        await settleWithin(settling, ABORTED_ATTEMPT_SETTLE_MS);
        continue;
      }

      this.mutate(
        handle,
        (current) => {
          const execution = current.execution.nodes[node.id];
          assertLegal(execution, `node not in execution: ${node.id}`);
          assertLegal(
            execution.status === "available",
            `cannot execute ${node.run.kind} node ${node.id}: status is ${execution.status}`,
          );
          current.execution.nodes[node.id] = {
            ...execution,
            status: "running",
            startedAt: now(),
          };
        },
        false,
      );

      const deterministicExecutionGeneration = state.executionGeneration;
      const deterministicTimeoutSec = node.control?.timeoutSec;
      const deterministicRunningExecution = state.execution.nodes[node.id];
      const deterministicRemainingMs = deterministicRunningExecution
        ? remainingActiveExecutionMs(
            deterministicRunningExecution,
            deterministicTimeoutSec,
            Date.now(),
          )
        : undefined;
      const deterministicKindLabel = deterministicNodeKindLabel(node.run.kind);

      let capturedOutput: NodeOutput | undefined;
      let capturedInputOrigins: Record<string, BindingOrigin[]> | undefined;
      let checkEvidence: CheckEvidence | undefined;
      let deterministicOutcome:
        | { kind: "success" }
        | { kind: "check-fail"; message: string; onFail: "escalate" | "fail" }
        | { kind: "failure"; message: string };
      // One controller per attempt: aborting is a one-way door, and the retry
      // that may follow is a fresh execution that must not start pre-aborted.
      const deterministicAbort = new AbortController();
      let runPromise: Promise<NodeExecutorResult> | undefined;
      try {
        const executor = state.nodeExecutor;
        if (!executor)
          throw new Error(`no NodeExecutor configured for ${node.run.kind} node ${node.id}`);
        const resolved = resolveNodeBindings(
          state.flowDef,
          node,
          snapshotMap(state.execution),
          state.flowInputOrigins,
        );
        capturedInputOrigins = resolved.origins;
        runPromise = executor.execute(
          { node, inputs: resolved.values, inputOrigins: resolved.origins },
          { signal: deterministicAbort.signal },
        );
        const result =
          deterministicRemainingMs !== undefined
            ? await raceWithTimeout(
                runPromise,
                Math.max(0, deterministicRemainingMs),
                `${deterministicKindLabel} node ${node.id} timed out after ${deterministicTimeoutSec}s`,
              )
            : await runPromise;
        const successfulStatuses = node.run.successExit ?? [0];
        const passed = successfulStatuses.includes(result.exitStatus);
        capturedOutput = deterministicNodeOutput(node, result, passed);
        const detail = passed ? "" : result.stderr.trim() || result.stdout.trim();
        const detailSuffix = detail ? `: ${detail}` : "";

        // A schema mismatch on a passing process is diagnostic state, not a check
        // verdict — shared by both kinds so a check whose *output* is malformed
        // (as opposed to whose exit status failed) throws into the generic
        // infrastructure-failure path below rather than fabricating a fail verdict.
        if (passed) {
          const validation = validateConsumedNodeOutput(
            state.flowDef,
            node.id,
            capturedOutput.fields,
          );
          if (!validation.valid) {
            throw new Error(
              `output does not match its consumed schema: ${validation.issues.join("; ")}`,
            );
          }
        }

        if (isCheckNode(node)) {
          // A clean process exit outside `successExit` is a fail *verdict*, not an
          // infrastructure failure — it carries evidence and consults `onFail`,
          // never the generic throw-based failure path below.
          checkEvidence = deriveCheckEvidence(
            resolved,
            {
              runtime: node.run.runtime ?? "shell",
              command: node.run.command,
              cwd: node.run.cwd,
              successExit: successfulStatuses,
              exitStatus: result.exitStatus,
              passed,
            },
            now(),
          );
          deterministicOutcome = passed
            ? { kind: "success" }
            : {
                kind: "check-fail",
                message: `Check node ${node.id} failed with exit status ${result.exitStatus}${detailSuffix}`,
                onFail: node.run.onFail,
              };
        } else if (!passed) {
          throw new Error(
            `Function node ${node.id} exited with unsuccessful exit status ${result.exitStatus}${detailSuffix}`,
          );
        } else {
          deterministicOutcome = { kind: "success" };
        }
      } catch (error) {
        // Anything that lands here — spawn failure, timeout, thrown output-schema
        // mismatch — produced no verdict at all, so it is always a plain node
        // failure and never reaches a check's `onFail` escalation.
        if (error instanceof NodeTimeoutError) {
          // Counterpart to the sub-flow branch's `target.cancel()`: we stopped
          // waiting, so stop the work too. The promise is carried out of here so
          // the next spawn can wait for the kill to land — see the gate above.
          deterministicAbort.abort();
          abortedAttempt = runPromise;
          deterministicOutcome = { kind: "failure", message: error.message };
        } else {
          deterministicOutcome = {
            kind: "failure",
            message: `${deterministicKindLabel} node ${node.id} failed: ${error instanceof Error ? error.message : String(error)}`,
          };
        }
      }

      // Late-settlement rejection: a hydrate/cancel/disconnect/start that
      // landed while this await was in flight invalidates whatever we just
      // computed — bail before writing either outcome.
      if (this.s(handle).executionGeneration !== deterministicExecutionGeneration) {
        // Reached only after the attempt settled, so for a process-spawning
        // executor there is nothing left to kill; it releases an executor that
        // resolves before its work is done. Cancelling a *live* attempt is not
        // possible here — this await is what a cancel cannot interrupt. Leaving
        // by this door also skips the settle gate, so a timeout racing a cancel
        // can still hand a re-drain a process that is mid-escalation.
        deterministicAbort.abort();
        break;
      }

      if (deterministicOutcome.kind === "success") {
        this.mutate(
          handle,
          (current) => {
            const execution = current.execution.nodes[node.id];
            assertLegal(execution, `node not in execution: ${node.id}`);
            const completedAt = now();
            const outputHistory = appendAttempt(
              execution.outputHistory,
              "complete",
              execution.startedAt ?? completedAt,
              completedAt,
              capturedOutput,
              undefined,
              { check: checkEvidence },
            );
            current.execution.nodes[node.id] = {
              ...execution,
              status: "complete",
              completedAt,
              output: capturedOutput,
              outputHistory,
              inputOrigins: capturedInputOrigins,
            };
          },
          false,
        );
      } else if (deterministicOutcome.kind === "check-fail") {
        const { message: failureMessage, onFail } = deterministicOutcome;
        this.mutate(
          handle,
          (current) => {
            const execution = current.execution.nodes[node.id];
            assertLegal(execution, `node not in execution: ${node.id}`);
            assertLegal(checkEvidence, `check node ${node.id} completed without evidence`);
            const completedAt = now();
            const { retry, patch } = this.planAutomaticRetry(
              node,
              execution,
              failureMessage,
              completedAt,
              capturedOutput,
              undefined,
              checkEvidence,
            );
            if (retry) {
              current.execution.nodes[node.id] = {
                ...execution,
                ...patch,
                status: "available",
                error: undefined,
              };
            } else if (onFail === "fail") {
              current.execution.nodes[node.id] = {
                ...execution,
                ...patch,
                status: resolveTerminalFailureStatus(node),
                completedAt,
              };
            } else {
              // Parking hands the verdict to a human: `applyApproval` terminalizes
              // "rejected", while "approved" is an override that needs a reason and
              // completes the node — it never rewrites the evidence recorded here.
              const provenanceNote = checkEvidence.provenance
                ? ` (${checkEvidence.provenance})`
                : "";
              const approval: Approval = {
                requestedAt: now(),
                summary: `Check node ${node.id} failed with exit status ${checkEvidence.exitStatus}${provenanceNote}.`,
              };
              current.execution.nodes[node.id] = {
                ...execution,
                ...patch,
                status: "awaiting_approval",
                approval,
                approvalHistory: [...execution.approvalHistory, approval],
              };
            }
          },
          false,
        );
      } else {
        const failureMessage = deterministicOutcome.message;
        this.mutate(
          handle,
          (current) => {
            const execution = current.execution.nodes[node.id];
            assertLegal(execution, `node not in execution: ${node.id}`);
            const completedAt = now();
            const { retry, patch } = this.planAutomaticRetry(
              node,
              execution,
              failureMessage,
              completedAt,
              capturedOutput,
            );
            current.execution.nodes[node.id] = retry
              ? { ...execution, ...patch, status: "available", error: undefined }
              : { ...execution, ...patch, status: resolveTerminalFailureStatus(node), completedAt };
          },
          false,
        );
      }
    }

    return this.getExecution(handle);
  }

  // ── Private helpers ──

  /** Settle one decidable router per source ownership component before runtime side effects. */
  private settleAvailableRouters(handle: ConnectorHandle): boolean {
    const state = this.s(handle);
    const snapshots = snapshotMap(state.execution);
    const settlements: RouterSettlement[] = [];
    const availableRouterSnapshots = new Map(snapshots);
    const computed = computeFlowStateFromSnapshots(state.flowDef, snapshots);
    for (const nodeId of computed.available) {
      const execution = availableRouterSnapshots.get(nodeId);
      if (execution && execution.status !== "available") {
        availableRouterSnapshots.set(nodeId, { ...execution, status: "available" });
      }
    }
    const frontier = availableRouterFrontier(state.flowDef, availableRouterSnapshots);
    for (const node of frontier) {
      const resolution = evaluateRouterSelection(state.flowDef, node, snapshots);
      if ("unresolved" in resolution) continue;
      settlements.push({ routerNodeId: node.id, selectedTarget: resolution.selectedTarget });
    }
    if (settlements.length === 0) return false;

    const preparedSettlements = settlements.map((settlement) => {
      const output = routerSelectionOutput(settlement.selectedTarget);
      return {
        settlement,
        output,
        validation: validateConsumedNodeOutput(
          state.flowDef,
          settlement.routerNodeId,
          output.fields,
        ),
      };
    });
    const validSettlements = preparedSettlements.flatMap(({ settlement, validation }) =>
      validation.valid ? [settlement] : [],
    );
    const settlementPlan = [...validSettlements];
    let skippedNodeIds: string[] = [];
    if (settlementPlan.length > 0) {
      while (true) {
        skippedNodeIds = planRouterSkips(state.flowDef, snapshots, settlementPlan);
        const settledIds = new Set(settlementPlan.map(({ routerNodeId }) => routerNodeId));
        const losingRouters = skippedNodeIds.flatMap((nodeId) => {
          const node = state.flowDef.nodes.find(({ id }) => id === nodeId);
          return node && isRouterNode(node) && !settledIds.has(node.id) ? [node] : [];
        });
        if (losingRouters.length === 0) break;
        settlementPlan.push(
          ...losingRouters.map(({ id }) => ({ routerNodeId: id, selectedTarget: null })),
        );
      }
    }
    const settledAt = now();
    // Records no `inputOrigins` on purpose: a router resolves no bindings, and its
    // selection rides in `output.summary` — it emits no `output.fields` to bind to.
    this.mutate(
      handle,
      (current) => {
        for (const prepared of preparedSettlements) {
          const { settlement, output, validation } = prepared;
          const execution = current.execution.nodes[settlement.routerNodeId];
          assertLegal(execution, `node not in execution: ${settlement.routerNodeId}`);
          assertLegal(
            execution.status === "available",
            `cannot resolve router node ${settlement.routerNodeId}: status is ${execution.status}`,
          );
          if (!validation.valid) {
            const error: NodeError = {
              message: `Router node ${settlement.routerNodeId} failed: output does not match its consumed schema: ${validation.issues.join("; ")}`,
              recoverable: false,
              at: settledAt,
            };
            current.execution.nodes[settlement.routerNodeId] = {
              ...execution,
              status: resolveTerminalFailureStatus(
                findNode(current.flowDef, settlement.routerNodeId),
              ),
              startedAt: execution.startedAt ?? settledAt,
              completedAt: settledAt,
              output,
              error,
              errorHistory: [...execution.errorHistory, error],
            };
            continue;
          }
          current.execution.nodes[settlement.routerNodeId] = {
            ...execution,
            status: "complete",
            startedAt: execution.startedAt ?? settledAt,
            completedAt: settledAt,
            output,
          };
        }
        for (const skippedNodeId of skippedNodeIds) {
          const execution = current.execution.nodes[skippedNodeId];
          if (
            !execution ||
            (execution.status !== "not_started" && execution.status !== "available")
          ) {
            continue;
          }
          current.execution.nodes[skippedNodeId] = {
            ...execution,
            status: "skipped",
            completedAt: settledAt,
          };
        }
      },
      false,
    );
    return true;
  }

  private s(handle: ConnectorHandle): FlowAdapterState {
    return handle.state as FlowAdapterState;
  }

  /**
   * Central mutation pipeline: runs the mutator, recomputes derived
   * fields, updates the flow-level `status` if the mutation completed
   * or failed the flow, fires `onChange` once, and optionally signals the
   * {@link FlowAdapterStimulusBus}. The signal path is gated by the
   * `useBusForStimulus` flag set at connect time.
   */
  private mutate(
    handle: ConnectorHandle,
    fn: (s: FlowAdapterState) => unknown,
    signalStimulus = true,
  ): string {
    const s = this.s(handle);
    const previousExecution = s.lastSignaledExecution;
    // Detach from the last-signaled snapshot before fn runs: it may alias this
    // object, and fn's in-place `nodes[x] = {...}` would corrupt the "previous"
    // snapshot, hiding non-fast-path diffs from computeStimulus.
    s.execution = {
      ...s.execution,
      nodes: nodeExecutionRecord(Object.entries(s.execution.nodes)),
    };
    const result = fn(s);
    s.execution = this.recompute(s);
    if (s.onChange) s.onChange(s.execution);

    if (signalStimulus && s.useBusForStimulus && s.stimulusBus) {
      const stim = computeStimulus(previousExecution, s.execution);
      if (stim) {
        const fragment = renderStimulusPrompt(stim);
        const connectorId = s.connectorId ?? handle.id;
        // Fire-and-forget — the bus's signal() returns a promise that
        // resolves once the resulting turn drains, but the adapter must
        // not block agent tool-call completion on the next turn settling.
        // Swallow rejections so a downstream turn failure can't propagate
        // back into the synchronous mutate() pipeline.
        s.stimulusBus
          .signal(connectorId, {
            promptFragment: fragment,
            meta: { kind: stim.kind, runId: s.execution.runId },
          })
          .catch(() => {
            /* rejection surfaces via the bus's own logging path */
          });
        s.lastSignaledExecution = s.execution;
      }
    }

    // Recompute the opportunistic agent-node wake timer against the
    // just-mutated state — every mutation can change which node is running,
    // start a fresh `startedAt`, or move the flow to a terminal status.
    this.rearmWakeTimer(handle);

    return typeof result === "string" ? result : JSON.stringify({ ok: true });
  }

  /**
   * Recompute derived fields (`focus`, `done`, and the rolled-up flow
   * `status`) from the current node map, without touching per-node
   * state directly.
   */
  private recompute(s: FlowAdapterState): FlowExecution {
    const snapshots = snapshotMap(s.execution);
    const computed = computeFlowStateFromSnapshots(s.flowDef, snapshots);
    const routerGatedNodes = unsettledRouterBranchIds(s.flowDef, snapshots);

    // Recompute precondition admission and reports in both directions. Host retries stay
    // available through unfinished predecessors; retry lifecycle is separate.
    const nodes = nodeExecutionRecord(Object.entries(s.execution.nodes));
    for (const ns of computed.nodes) {
      const existing = nodes[ns.nodeId];
      if (!existing) continue;
      if (existing.status === "not_started" || existing.status === "available") {
        const definitionNode = s.flowDef.nodes.find(({ id }) => id === ns.nodeId);
        const hasFailedPreconditions = (ns.failedPreconditions?.length ?? 0) > 0;
        const status =
          existing.status === "available" &&
          ns.status === "not_started" &&
          !hasFailedPreconditions &&
          !routerGatedNodes.has(ns.nodeId) &&
          !(definitionNode && isRouterNode(definitionNode))
            ? "available"
            : ns.status;
        nodes[ns.nodeId] = {
          ...existing,
          status,
          failedPreconditions: hasFailedPreconditions ? ns.failedPreconditions : undefined,
        };
      }
    }

    // Roll up flow-level status when the computation tells us we're done,
    // or when a required node is stuck in `failed`.
    let flowStatus: FlowExecutionStatus = s.execution.status;
    if (flowStatus !== "cancelled") {
      const anyRequiredFailed = s.flowDef.nodes
        .filter((n) => isActiveRuntimeNode(n) && !nodeIsOptional(n))
        .some((n) => nodes[n.id]?.status === "failed");
      if (anyRequiredFailed) {
        flowStatus = "failed";
      } else if (computed.done) {
        flowStatus = "complete";
      } else if (flowStatus !== "paused") {
        flowStatus = "running";
      }
    }

    return {
      ...s.execution,
      status: flowStatus,
      nodes,
      focus: computed.focus,
      done: computed.done && flowStatus === "complete",
    };
  }

  // ── Agent-node deadline wake timer ────────────────────────────────────────
  //
  // Agent nodes have no `await` the adapter can wrap — it marks the node
  // started and waits for the model to call `complete_node`, which may never
  // come. The derived deadline (see `engine/node-deadline.ts`) is the source
  // of truth: it is a pure function of `startedAt` + `approvalHistory` +
  // `inputHistory`, so it is correct on its own after `hydrate()` or a
  // reconnect with no timer required. This wake timer exists only so a
  // genuinely hung run — by definition nothing else happening — still gets
  // *some* event that triggers a re-evaluation of that deadline.

  private clearWakeTimer(s: FlowAdapterState): void {
    if (s.wakeTimer !== null) {
      clearTimeout(s.wakeTimer);
      s.wakeTimer = null;
    }
  }

  /**
   * Whether `execution` counts as "actively running" for `control.timeoutSec`
   * evaluation. Ordinarily this is just `status === "running"`. The one
   * exception: a non-gate agent node whose approval was just decided
   * `approved` stays at `awaiting_approval` — `applyApproval` deliberately
   * does not restore `running` there, because `doCompleteNode`'s
   * `approvedPrior` check requires exactly that status (a mandatory-approval
   * node has no `runningAutonomous` fallback). Without this second clause the
   * timeout would pause for the approval wait (correctly, via
   * `remainingActiveExecutionMs` reading the now-resolved `approvalHistory`
   * entry) and then never resume, since nothing re-evaluates a node that
   * never looks "running" again.
   */
  private isDeadlineEligible(execution: NodeExecution): boolean {
    if (execution.status === "running") return true;
    return execution.status === "awaiting_approval" && execution.approval?.decision === "approved";
  }

  /**
   * Recomputes the pending wake timer against the current state: cancels any
   * previously-armed timer, then arms a fresh one for the earliest deadline
   * among currently `running` agent nodes with a `control.timeoutSec`. A
   * no-op when nothing is running against a timeout, or the flow is already
   * terminal. Called after every {@link mutate}, plus `connect()` and
   * `hydrate()` (which bypass `mutate()`).
   *
   * `disconnect()` clears the timer and bumps `executionGeneration`, but a
   * `mutate()` already in flight (or one that lands after teardown for any
   * other reason) still calls this on its way out — bail here on
   * `handle.status` so a post-disconnect mutation can never re-arm a timer
   * that would fire against a handle nothing is watching anymore.
   */
  private rearmWakeTimer(handle: ConnectorHandle): void {
    const s = this.s(handle);
    this.clearWakeTimer(s);
    if (handle.status === "disconnected") return;
    if (s.execution.done || s.execution.status === "failed" || s.execution.status === "cancelled") {
      return;
    }
    const nowMs = Date.now();
    let earliestRemainingMs: number | undefined;
    for (const node of s.flowDef.nodes) {
      if (!isAgentNode(node)) continue;
      const timeoutSec = node.control?.timeoutSec;
      if (timeoutSec === undefined) continue;
      const execution = s.execution.nodes[node.id];
      if (!execution || !this.isDeadlineEligible(execution)) continue;
      const remaining = remainingActiveExecutionMs(execution, timeoutSec, nowMs);
      if (remaining === undefined) continue;
      if (earliestRemainingMs === undefined || remaining < earliestRemainingMs) {
        earliestRemainingMs = remaining;
      }
    }
    if (earliestRemainingMs === undefined) return;
    const delayMs = Math.max(0, earliestRemainingMs);
    const timer = setTimeout(() => {
      s.wakeTimer = null;
      void this.evaluateAgentNodeDeadlines(handle);
    }, delayMs);
    timer.unref?.();
    s.wakeTimer = timer;
  }

  /**
   * Re-checks every `running` agent node's derived deadline and, for any that
   * have expired, either auto-retries (bounded by `control.retries`, composing
   * with the existing `doFailNode(recoverable: true)` and `retryNode`
   * pathways) or terminally fails the node. Safe to call at any time — the
   * derived deadline means this is a no-op unless something has actually
   * expired. Goes through {@link mutate}, so a state change here republishes
   * `onChange`/stimulus exactly like any other mutation, waking a fresh turn
   * when the node retries or the flow rolls up to `failed`.
   */
  private async evaluateAgentNodeDeadlines(handle: ConnectorHandle): Promise<void> {
    const s = this.s(handle);
    const nowMs = Date.now();
    const expired = s.flowDef.nodes.filter((node) => {
      if (!isAgentNode(node)) return false;
      const timeoutSec = node.control?.timeoutSec;
      if (timeoutSec === undefined) return false;
      const execution = s.execution.nodes[node.id];
      if (!execution || !this.isDeadlineEligible(execution)) return false;
      const remaining = remainingActiveExecutionMs(execution, timeoutSec, nowMs);
      return remaining !== undefined && remaining <= 0;
    });
    for (const node of expired) {
      this.mutate(
        handle,
        (current) => {
          const execution = current.execution.nodes[node.id];
          // Already handled (e.g. a previous iteration's mutate() resolved
          // this via a different path) — nothing to do.
          if (!execution || !this.isDeadlineEligible(execution)) return;
          const completedAt = now();
          const message = `Agent node ${node.id} timed out after ${node.control?.timeoutSec}s`;
          const { retry, patch } = this.planAutomaticRetry(
            node,
            execution,
            message,
            completedAt,
            execution.output,
          );
          current.execution.nodes[node.id] = retry
            ? { ...execution, ...patch, status: "available", error: undefined }
            : { ...execution, ...patch, status: resolveTerminalFailureStatus(node), completedAt };
        },
        true,
      );
    }
  }

  /**
   * Records one failed attempt on `nodeId`'s execution and decides whether
   * the automatic bounded retry (`control.retries`) still has budget. Shared
   * by every automatic-failure seam (agent-node timeout, function/check
   * execution/timeout failure, sub-flow execution/timeout failure, and a
   * check's fail verdict) so the budget — derived from prior `'failed'`
   * entries in `outputHistory`, not a separate durable counter — is computed
   * once, the same way, everywhere.
   *
   * Returns the `NodeExecution` patch (error/errorHistory/outputHistory/
   * output) plus whether budget remains. The terminal *failure* status is
   * uniform across kinds and computed by {@link resolveTerminalFailureStatus};
   * the caller still writes the status because its non-failure outcomes differ
   * (a check's escalation parks at `awaiting_approval`, a surviving budget
   * returns to `available`). A terminal failure whose node has any
   * `control.retries` configured carries `recoverable: true` so the manual
   * `retryNode` operator override — which sits outside this budget entirely —
   * remains usable once the automatic budget runs out; a node that will be
   * written `skipped` never claims it, because a skip is not retryable.
   *
   * @param sourceErrorAtIso - The failure's own timestamp, when the caller has
   *   one more precise than `completedAtIso` (e.g. a sub-flow child reports
   *   its own `error.at`, which can predate this node's parent-side
   *   `completedAt` by however long the result took to propagate). Defaults
   *   to `completedAtIso` for callers (agent-timeout, function-node) whose
   *   failure message is synthesized at the moment of detection.
   * @param checkEvidence - Derived evidence for a check's fail verdict, so it
   *   rides on the same appended attempt as the failure it explains.
   */
  private planAutomaticRetry(
    node: FlowNode,
    execution: NodeExecution,
    message: string,
    completedAtIso: string,
    capturedOutput: NodeOutput | undefined,
    sourceErrorAtIso?: string,
    checkEvidence?: CheckEvidence,
  ): { retry: boolean; patch: Partial<NodeExecution> } {
    const retries = node.control?.retries;
    const priorFailures = countFailedAttempts(execution.outputHistory);
    const retry = hasAutomaticRetryBudget(priorFailures, retries);
    // Gated on this node's OWN `control.retries`, never a delegated child's
    // reported `recoverable` (see the sub-flow caller above, which discards
    // `result.error.recoverable` on purpose) — parent recoverability follows
    // the parent's own control, so the manual `retryNode` escape hatch opens
    // once the automatic budget is exhausted only when *this* node declares
    // a retry budget of its own. Pinned by sub-flow-node.test.ts's
    // "does not propagate a child's own recoverable flag" case.
    const error: NodeError = {
      message,
      recoverable: !retry && !!retries && resolveTerminalFailureStatus(node) === "failed",
      at: sourceErrorAtIso ?? completedAtIso,
    };
    const outputHistory = appendAttempt(
      execution.outputHistory,
      "failed",
      execution.startedAt ?? completedAtIso,
      completedAtIso,
      capturedOutput,
      error,
      { check: checkEvidence },
    );
    return {
      retry,
      patch: {
        error,
        errorHistory: [...execution.errorHistory, error],
        outputHistory,
        output: capturedOutput ?? execution.output,
      },
    };
  }

  /**
   * Build the selected agent node's complete turn briefing plus upstream context.
   */
  private buildHandoff(s: FlowAdapterState, nodeId: string): string {
    const target = findNode(s.flowDef, nodeId);
    assertLegal(isAgentNode(target), `node is not an active agent node: ${nodeId}`);
    const targetExecution = s.execution.nodes[nodeId];
    assertLegal(targetExecution, `node not in execution: ${nodeId}`);
    assertLegal(
      targetExecution.status === "available" || targetExecution.status === "running",
      `cannot build handoff for ${nodeId}: status is ${targetExecution.status}`,
    );

    const sections = [`# Agent node: ${nodeLabel(target)}`, `Node ID: ${target.id}`];
    const resolved = resolveNodeBindings(
      s.flowDef,
      target,
      snapshotMap(s.execution),
      s.flowInputOrigins,
    );
    const instruction = nodeInstruction(target);
    if (instruction) {
      sections.push(
        `## Instruction\n\n${interpolateNodeInstruction(instruction, resolved.values)}`,
      );
    }

    if (Object.keys(resolved.values).length > 0) {
      sections.push(
        `## Supplied bindings\n\n\`\`\`json\n${JSON.stringify(resolved.values, null, 2)}\n\`\`\``,
        `## Binding origins\n\n\`\`\`json\n${JSON.stringify(resolved.origins, null, 2)}\n\`\`\``,
      );
    }

    if (targetExecution.outputCorrection) {
      sections.push(
        `## Output correction required\n\nThis node has one corrective output call available. Fix every issue before calling \`complete_node\` again:\n${targetExecution.outputCorrection.issues
          .map((issue) => `- ${issue}`)
          .join("\n")}`,
      );
    }

    const assets = nodeAssets(target);
    if (assets.length > 0) {
      sections.push(`## Required assets\n\n${assets.map((asset) => `- ${asset}`).join("\n")}`);
    }

    const deps = flowPredecessors(s.flowDef, nodeId);
    const supplied = suppliedProducerFields(resolved);
    const handoff: string[] = [];
    for (const depId of deps) {
      const exec = s.execution.nodes[depId];
      const output = exec?.output;
      if (!output) continue;
      const artifacts =
        output.artifacts.length > 0
          ? `\nArtifacts: ${output.artifacts.map((a) => a.uri).join(", ")}`
          : "";
      const concerns =
        output.concerns && output.concerns.length > 0
          ? `\nConcerns: ${output.concerns.join("; ")}`
          : "";
      const predecessor = s.flowDef.nodes.find((n) => n.id === depId);
      const label = predecessor ? nodeLabel(predecessor) : depId;
      const summary =
        predecessor && isRouterNode(predecessor)
          ? (routerSelectionHandoffSummary(predecessor, exec) ?? output.summary)
          : output.summary;
      // Gated on `complete` to match the binding layer: `buildFlowExpressionContext`
      // exposes `output.fields` only for a completed producer, and a skip path
      // spreads the prior execution — so a failed-then-skipped optional node still
      // carries captured output the flow's own contract says it never produced.
      const fields =
        exec?.status === "complete" ? upstreamFieldsBlock(predecessor, output, supplied) : "";
      handoff.push(`### ${label} (${exec?.status})\n${summary}${fields}${artifacts}${concerns}`);
    }

    sections.push(
      `## Upstream handoff\n\n${handoff.length > 0 ? handoff.join("\n\n") : "(no prior context)"}`,
    );
    return sections.join("\n\n");
  }
}

// ── Pure helpers ────────────────────────────────────────────────────────────

/** IDs of nodes connected to `nodeId` via incoming flow-type edges. */
function flowPredecessors(flowDef: FlowDefinition, nodeId: string): string[] {
  return flowDef.edges.filter((e) => e.target === nodeId && e.type === "flow").map((e) => e.source);
}

/** Render a completed router choice without leaking its durable selection marker. */
function routerSelectionHandoffSummary(
  router: FlowNode,
  execution: NodeExecution | undefined,
): string | undefined {
  const selection = persistedRouterSelection(router, execution);
  if (!selection) return undefined;
  return selection.selectedTarget === null
    ? "Router completed without selecting a target."
    : `Router selected ${selection.selectedTarget}.`;
}

/**
 * Per-field cap on one rendered upstream value, in characters. Deliberately
 * per-field rather than one budget for the whole block: a shared budget lets a
 * single huge first field swallow the allowance and erase every later field.
 */
const HANDOFF_FIELD_VALUE_MAX_CHARS = 500;

/** Hard upper bound on how many declared fields one predecessor contributes to a handoff. */
const HANDOFF_FIELD_MAX_COUNT = 20;

/**
 * Top-level field names a node publishes through `contract.output.fields`, in
 * the order the schema declares them.
 *
 * Declared-only is the invariant: `contract/schema.ts` already refuses
 * `nodes.X.output.fields.Y` unless `Y` sits under `properties`, so declaring a
 * field is the single existing opt-in for "downstream may see this". Rendering
 * captured-but-undeclared keys would turn the handoff into a second,
 * unvalidated contract surface that authors cannot version.
 *
 * Gotcha: a schema without `properties` (a bare `{ "type": "object" }`)
 * publishes nothing — consistent with no consumer being able to bind it either.
 */
function declaredOutputFieldNames(node: FlowNode | undefined): string[] {
  const schema = node?.contract?.output?.fields;
  if (!schema || typeof schema !== "object" || Array.isArray(schema)) return [];
  const properties = (schema as Record<string, unknown>).properties;
  if (!properties || typeof properties !== "object" || Array.isArray(properties)) return [];
  return Object.keys(properties);
}

/**
 * What a consumer's own bindings already put in front of it, as
 * `"<nodeId> <root> <serializedValue>"` triples — one binding's provenance and
 * its resolved value, kept together.
 *
 * The pairing is the point. Provenance alone over-suppresses:
 * `${nodes.p.output.fields.score > 5}` reads `score` but shows only `true`, and
 * `…fields.nested.a` shows only that leaf, so dropping the field would delete
 * the very value this block exists to surface while claiming it was already
 * shown. Value identity alone under-discriminates: an unrelated literal that
 * happens to equal a field's value is not that field being shown twice. Two
 * independent sets would still admit the second failure across *different*
 * bindings — one reading the root, another coincidentally carrying the value —
 * so both halves must come from the same binding to count.
 */
function suppliedProducerFields(resolved: {
  origins: Record<string, BindingOrigin[]>;
  values: Record<string, unknown>;
}): Set<string> {
  const supplied = new Set<string>();
  for (const [name, originList] of Object.entries(resolved.origins)) {
    const serialized = JSON.stringify(resolved.values[name]);
    if (serialized === undefined) continue;
    for (const origin of originList) {
      if (origin.source !== "node-output") continue;
      // Keyed on the ROOT segment, so a deep read marks its whole top-level
      // field as read. Node ids and field names cannot contain a space (the
      // expression tokenizer forbids it), so this encoding cannot alias.
      const root = origin.path.split(".")[4];
      if (root) supplied.add(`${origin.nodeId} ${root} ${serialized}`);
    }
  }
  return supplied;
}

/**
 * Render one predecessor's declared output fields for `## Upstream handoff`, so
 * an agent reading a producer as context sees its typed answer and not only its
 * prose — the typed answer was previously reachable only by binding it.
 *
 * Invariants: only fields the producer declared *and* actually emitted render;
 * at most `HANDOFF_FIELD_MAX_COUNT` of them, each value capped at
 * `HANDOFF_FIELD_VALUE_MAX_CHARS`. A field is dropped only when one of the
 * consumer's own bindings both read it and resolved to that exact value — then,
 * and only then, is "already shown under Supplied bindings" true of it. Every
 * omission is stated in the block rather than left silent.
 *
 * Gotcha: a truncated value is deliberately not parseable JSON — it is prose
 * for a reader, and re-serializing a partial value would hide the cut.
 *
 * Returns `""` when nothing would render — including when every declared field
 * is suppressed or absent — so no empty `Fields:` heading appears.
 */
function upstreamFieldsBlock(
  predecessor: FlowNode | undefined,
  output: NodeOutput,
  supplied: Set<string>,
): string {
  const fields = output.fields;
  if (!predecessor || !fields) return "";

  const renderable: Array<[string, string]> = [];
  let suppressed = 0;
  for (const name of declaredOutputFieldNames(predecessor)) {
    if (!Object.hasOwn(fields, name)) continue;
    const serialized = JSON.stringify(fields[name]) ?? "null";
    if (supplied.has(`${predecessor.id} ${name} ${serialized}`)) {
      suppressed += 1;
      continue;
    }
    renderable.push([name, serialized]);
  }
  if (renderable.length === 0) return "";

  const lines = renderable.slice(0, HANDOFF_FIELD_MAX_COUNT).map(([name, serialized]) => {
    const value =
      serialized.length > HANDOFF_FIELD_VALUE_MAX_CHARS
        ? `${serialized.slice(0, HANDOFF_FIELD_VALUE_MAX_CHARS)} … [truncated from ${serialized.length} chars]`
        : serialized;
    return `- ${name}: ${value}`;
  });
  const overflow = renderable.length - lines.length;
  if (overflow > 0) {
    lines.push(
      `- … ${overflow} more declared field(s) not shown (${HANDOFF_FIELD_MAX_COUNT}-field cap)`,
    );
  }
  if (suppressed > 0) {
    lines.push(
      `- (${suppressed} declared field(s) omitted — already shown under "Supplied bindings")`,
    );
  }
  return `\nFields:\n${lines.join("\n")}`;
}

/** `passed` is the caller's already-computed `successExit` membership test — never recomputed here. */
function deterministicNodeOutput(
  node: DeterministicFlowNode,
  result: NodeExecutorResult,
  passed: boolean,
): NodeOutput {
  const kindLabel = deterministicNodeKindLabel(node.run.kind);
  const capturedFields: Array<[string, unknown]> = [];
  for (const channel of ["stdout", "stderr"] as const) {
    const destination = node.run.capture?.[channel];
    if (!destination) continue;
    const match = /^fields\.([^.]+)$/.exec(destination);
    if (!match?.[1]) {
      throw new Error(
        `${kindLabel} node ${node.id} run.capture.${channel} destination "${destination}" is invalid; expected fields.<name>`,
      );
    }
    capturedFields.push([match[1], result[channel]]);
  }
  const fields = Object.fromEntries(capturedFields);
  const summary =
    node.run.kind === "check"
      ? `Check node ${node.id} ${passed ? "passed" : "failed"} with exit status ${result.exitStatus}.`
      : `Function node ${node.id} exited with status ${result.exitStatus}.`;
  return {
    summary,
    artifacts: [],
    ...(Object.keys(fields).length > 0 ? { fields } : {}),
  };
}

interface JsonSpanScan {
  /** The sole complete top-level span, when the reply contains exactly one and nothing else bracket-like. */
  span?: string;
  /** Why extraction refused — set for a structural refusal, absent for bracket-free prose. */
  ambiguity?: string;
}

/**
 * Locate the reply's single balanced top-level JSON structure, refusing on any
 * structural ambiguity rather than guessing. Rationale for each refusal rule:
 * `_devlog/notes/2026-08-16-subprompt-narrated-json-extraction.md`.
 * Gotcha: quotes toggle string tracking only *inside* a span, so a double quote in
 * the surrounding prose is ignored rather than swallowing the payload.
 */
function findSoleJsonSpan(text: string): JsonSpanScan {
  const spans: Array<{ text: string; end: number }> = [];
  const stack: string[] = [];
  let spanStart: number | null = null;
  let inString = false;
  let escaped = false;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    if (inString) {
      if (escaped) escaped = false;
      else if (char === "\\") escaped = true;
      else if (char === '"') inString = false;
      continue;
    }
    if (stack.length > 0 && char === '"') {
      inString = true;
    } else if (char === "{" || char === "[") {
      if (stack.length === 0) spanStart = i;
      stack.push(char === "{" ? "}" : "]");
    } else if (char === "}" || char === "]") {
      if (stack.length === 0) {
        return { ambiguity: "a closing bracket appears outside any JSON structure" };
      }
      if (stack.pop() !== char) return { ambiguity: "mismatched bracket" };
      if (stack.length === 0 && spanStart !== null) {
        spans.push({ text: text.slice(spanStart, i + 1), end: i + 1 });
        spanStart = null;
      }
    }
  }

  if (stack.length > 0 || inString) return { ambiguity: "unterminated JSON structure" };
  if (spans.length > 1) {
    return {
      ambiguity: `found ${spans.length} candidate JSON values; refusing to guess which is the answer`,
    };
  }
  const sole = spans[0];
  // Plain prose is the pre-existing failure, not an ambiguity — leave it undecorated.
  if (!sole) return {};
  const afterSpan = text.slice(sole.end);
  const lineBreak = afterSpan.indexOf("\n");
  const restOfLine = lineBreak === -1 ? afterSpan : afterSpan.slice(0, lineBreak);
  if (restOfLine.trim() !== "") {
    return { ambiguity: "the candidate JSON value is embedded mid-sentence" };
  }
  return { span: sole.text };
}

/**
 * Pick the JSON payload out of a subprompt's reply — a **fail-closed** tolerance
 * for a fenced or narrated reply, never a repair pass.
 *
 * Invariant: the fallback runs only when the payload used today does not parse, so
 * every currently-working reply yields a byte-identical payload. Refusal returns
 * that payload, leaving the caller's ordinary parse failure to stand, and sets
 * `ambiguity` so the error can say why. The guarantee is *structural*, not semantic
 * — a refusal shaped like a narrated answer is still accepted. Full rationale and
 * the residual limit: `_devlog/notes/2026-08-16-subprompt-narrated-json-extraction.md`.
 */
function extractSubpromptJsonPayload(text: string): { payload: string; ambiguity?: string } {
  const parses = (value: string): boolean => {
    try {
      JSON.parse(value);
      return true;
    } catch {
      return false;
    }
  };
  const trimmed = text.trim();
  const fenceMatch = /^```(?:json)?\s*([\s\S]*?)\s*```$/.exec(trimmed);
  const direct = fenceMatch?.[1] ?? trimmed;
  if (parses(direct)) return { payload: direct };

  const scan = findSoleJsonSpan(direct);
  if (scan.span !== undefined) {
    if (parses(scan.span)) return { payload: scan.span };
    return { payload: direct, ambiguity: "the sole candidate JSON value did not parse" };
  }
  return scan.ambiguity ? { payload: direct, ambiguity: scan.ambiguity } : { payload: direct };
}

/**
 * Build a subprompt node's output from its isolated driver's raw text. With no
 * declared output contract the response is prose, carried verbatim as the
 * summary. With one declared, the response is parsed as JSON into `fields` —
 * the runtime's only lever for typed output, since a subprompt has no
 * corrective reprompt to fall back on. A parse failure throws, landing the
 * caller in the same ordinary node-failure path as any other subprompt error.
 *
 * The reply stays in `summary` either way. `build_handoff` does render a
 * predecessor's fields, but only the *declared* ones, capped at 500 characters
 * each and at 20 per predecessor — and a contract without `properties`
 * publishes none at all. A boilerplate summary here would leave every one of
 * those cases with a completed node and no answer for a consumer that reads it
 * as context rather than binding it.
 */
function subpromptNodeOutput(node: SubpromptFlowNode, text: string): NodeOutput {
  const outputSchema = node.contract?.output?.fields;
  if (!outputSchema) return { summary: text.trim(), artifacts: [] };
  const extracted = extractSubpromptJsonPayload(text);
  const parsed = safeParseFields(extracted.payload);
  if (!parsed.fields) {
    const detail = extracted.ambiguity
      ? `${parsed.issues.join("; ")} (${extracted.ambiguity})`
      : parsed.issues.join("; ");
    throw new Error(`Subprompt node ${node.id} response is not valid JSON: ${detail}`);
  }
  return {
    summary: text.trim(),
    artifacts: [],
    fields: parsed.fields,
  };
}

function safeParseStringArray(value: unknown): string[] {
  if (!value) return [];
  if (Array.isArray(value)) return value.map(String);
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch {
      return [];
    }
  }
  return [];
}

const JsonFieldsSchema = z.record(z.string(), z.json());

interface ParsedFields {
  fields?: Record<string, unknown>;
  issues: string[];
}

function safeParseFields(value: unknown): ParsedFields {
  let parsed = value;
  if (typeof value === "string") {
    try {
      parsed = JSON.parse(value);
    } catch {
      return { issues: ["fields: malformed JSON; expected an object"] };
    }
  }
  const result = JsonFieldsSchema.safeParse(parsed);
  if (!result.success) {
    return {
      issues: result.error.issues.map((issue) => {
        const suffix = issue.path.map(String).join(".");
        return `fields${suffix ? `.${suffix}` : ""}: ${issue.message}`;
      }),
    };
  }
  return { fields: result.data, issues: [] };
}

function safeParseArtifacts(value: unknown): ArtifactRef[] {
  if (!value) return [];
  const raw = typeof value === "string" ? safeJsonArray(value) : Array.isArray(value) ? value : [];
  return raw
    .filter((r): r is Record<string, unknown> => !!r && typeof r === "object")
    .map((r: Record<string, unknown>) => ({
      uri: String(r.uri ?? ""),
      connectorId: String(r.connectorId ?? "workspace"),
      kind: (r.kind as ArtifactRef["kind"]) ?? "other",
      lifetime: (r.lifetime as ArtifactRef["lifetime"]) ?? "session",
      producedBy: String(r.producedBy ?? ""),
    }));
}

function safeJsonArray(value: string): unknown[] {
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

// Re-export `isBlocking` for convenience so callers don't need two imports
// when reasoning about node state blocking semantics.
export { isBlocking };

/**
 * Creates a new FlowAdapter instance.
 * @returns Configured FlowAdapter ready to be registered in the connector registry.
 * @docLink packages/factory-assets/api-reference#flow-connector-factory
 */
export function createConnector(): FlowAdapter {
  return new FlowAdapter();
}
